import VatJobWorkflow from './VatJobWorkflow'
import { useVatIntakePrices } from '../lib/useVatIntakePrices'
import VatIntakeTotals from './VatIntakeTotals'
import React, { useState, useEffect, useCallback } from 'react'
import StatusPill from './StatusPill'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { calcMins, fmtDate, fmtDuration, isGeneralJob, JOB_FINANCIAL_SELECT, JOB_STATUS_OPTIONS, jobFinancials, jobPartsTotals, jobPaymentMeta, jobProgressMeta, jobServiceScopeSummary, jobServiceSummary, jobStatusMeta, normaliseJobPartsUsed, normaliseJobServiceSnapshot, normaliseJobStatus, normalisePriceAdjustments, priceAdjustmentAmount } from '../lib/utils'
import { LOYALTY_WORKSHOP_COLUMNS, jobCanReceiveLoyaltyReward, loyaltyProgressForCustomer, loyaltyRuleSummary } from '../lib/loyalty'
import { useAuth } from '../lib/AuthContext'
import { isSuspensionService, jobServicePriceLabel } from '../lib/serviceMenu'
import GeneralJobModal from './GeneralJobModal'
import GeneralExtraServiceLinePanel from './GeneralExtraServiceLinePanel'
import JobEvidence from './JobEvidence'
import JobStockUsagePanel from './JobStockUsagePanel'
import JobTimeline, { jobNextAction } from './JobTimeline'
import { setupPayloadFromForm } from './SetupRecordForm'
import { isServiceKitPartRow, mergeJobPartRows, partRowFromServiceKit } from '../lib/stock'
import { extraServiceLineBillableTotal, extraServiceLineJobScopeRows } from '../lib/generalExtraServiceLines'
import { canOverrideScheduleCapacity, filterLifecycleStatusOptionsForProfile, lifecycleActionForStatusChange, lifecycleNoteForAction, runJobLifecycleAction, runJobScheduleUpdate, runServiceJobCreateWithSchedule, statusForDirectJobSave } from '../lib/jobLifecycle'
import { formatPlannedMinutes, plannedMinutesForJob } from '../lib/capacityPlanning'

const BOOL_FIELDS = [
  'sc_lower_leg','sc_air_spring','sc_damper','sc_air_can','sc_full_shock',
  'sc_bushing','sc_travel','sc_seal_upgrade','sc_retune','sc_setup','sc_dropper_service',
  'ins_stanchion','ins_csu','ins_bushing_play','ins_air_shaft','ins_damp_shaft',
  'ins_eyelet','ins_bearing','ins_wiper','ins_oil_contam','ins_scoring',
  'ins_adjusters','ins_lockout',
  'qc_torque','qc_pressure','qc_clickers','qc_travel','qc_topout',
  'qc_oil_leak','qc_air_loss','qc_adjusters','qc_baseline','qc_sticker'
]

const TEMPLATE_FIELD_LABELS = {
  sc_lower_leg: 'Lower leg service',
  sc_air_spring: 'Air spring service',
  sc_damper: 'Damper rebuild',
  sc_air_can: 'Air can service',
  sc_full_shock: 'Full shock service',
  sc_bushing: 'Bushing / hardware replacement',
  sc_travel: 'Travel change',
  sc_seal_upgrade: 'Seal upgrade',
  sc_retune: 'Custom tune / revalve',
  sc_setup: 'Setup session only',
  sc_dropper_service: 'Seatpost / dropper service',
  ins_stanchion: 'Stanchion / shaft condition',
  ins_csu: 'CSU / crown / steerer',
  ins_bushing_play: 'Bushing play',
  ins_air_shaft: 'Air shaft wear',
  ins_damp_shaft: 'Damper shaft wear',
  ins_eyelet: 'Eyelet / hardware wear',
  ins_bearing: 'Bearing mount condition',
  ins_wiper: 'Wiper / foam ring',
  ins_oil_contam: 'Oil contamination',
  ins_scoring: 'Internal scoring / damage',
  ins_adjusters: 'Adjusters functioning',
  ins_lockout: 'Lockout / threshold',
  qc_torque: 'Torque settings confirmed',
  qc_pressure: 'Pressures / spring installed',
  qc_clickers: 'Clickers reset',
  qc_travel: 'Full travel smooth',
  qc_topout: 'No topout / knock',
  qc_oil_leak: 'No oil leaks',
  qc_air_loss: 'No air loss detected',
  qc_adjusters: 'All adjusters functional',
  qc_baseline: 'Returned to customer baseline',
  qc_sticker: 'FLI sticker applied',
}

const SCOPE_FIELDS = [
  'sc_lower_leg','sc_air_spring','sc_damper','sc_air_can','sc_full_shock',
  'sc_bushing','sc_travel','sc_seal_upgrade','sc_retune','sc_setup','sc_dropper_service'
]

const SETTINGS_FIELDS = [
  { key: 'air', before: 'b_air', after: 'a_air', label: 'Air Pressure / Spring Rate', group: 'Spring', placeholder: 'e.g. 75 psi' },
  { key: 'sag', before: 'b_sag', after: 'a_sag', label: 'Sag %', group: 'Spring', placeholder: 'e.g. 30%' },
  { key: 'preload', before: 'b_preload', after: 'a_preload', label: 'Coil Preload (turns)', group: 'Spring', placeholder: 'e.g. 2 turns' },
  { key: 'tokens', before: 'b_tokens', after: 'a_tokens', label: 'Tokens / Volume Spacers', group: 'Spring', placeholder: 'e.g. 2 tokens' },
  { key: 'lsc', before: 'b_lsc', after: 'a_lsc', label: 'LSC', group: 'Compression', placeholder: 'e.g. 8 clicks from open' },
  { key: 'hsc', before: 'b_hsc', after: 'a_hsc', label: 'HSC', group: 'Compression', placeholder: 'e.g. 3 turns from closed' },
  { key: 'pedal', before: 'b_pedal', after: 'a_pedal', label: 'Pedal / Lockout / Threshold', group: 'Compression', placeholder: 'e.g. Position 2' },
  { key: 'hbo', before: 'b_hbo', after: 'a_hbo', label: 'HBO / HBC / ABO', group: 'Compression', placeholder: 'if applicable' },
  { key: 'lsr', before: 'b_lsr', after: 'a_lsr', label: 'LSR / Rebound', group: 'Rebound', placeholder: 'e.g. 10 clicks from open' },
  { key: 'hsr', before: 'b_hsr', after: 'a_hsr', label: 'HSR', group: 'Rebound', placeholder: 'if applicable' },
]

const SERVICE_LOG_FIELDS = [
  { key: 'shim_torque', label: 'Shim Stack Torque (Nm)' },
  { key: 'ifp_depth', label: 'IFP Depth (mm)' },
  { key: 'ifp_psi', label: 'IFP Pressure (psi)' },
  { key: 'oil_l', label: 'Lube Oil - Left (ml)' },
  { key: 'oil_r', label: 'Lube Oil - Right (ml)' },
  { key: 'damp_vol', label: 'Damper Oil Volume (ml)' },
  { key: 'seal_torque', label: 'Seal Head Torque (Nm)' },
  { key: 'topcap_torque', label: 'Top Cap Torque (Nm)' },
  { key: 'shaft_torque', label: 'Foot / Shaft Nut Torque (Nm)' },
  { key: 'travel_config', label: 'Travel Config / Stroke', placeholder: 'e.g. 160mm' },
  { key: 'mount_hw', label: 'Mount Hardware Size', placeholder: 'e.g. 8mm eyelet' },
  { key: 'damp_oil', label: 'Damping Oil Used', placeholder: 'e.g. Fox Float Fluid 20wt' },
  { key: 'aircan_grease', label: 'Grease / Lubricant Used', placeholder: 'e.g. Slick Honey' },
  { key: 'kit', label: 'Service Kit Used', placeholder: 'e.g. 00.4318.037.017 - Vivid Coil Service Kit' },
  { key: 'spring_hw', label: 'Spring Hardware / Reducer Fitted', placeholder: 'e.g. 50mm reducer' },
  { key: 'bleed', label: 'Bleed Method / Pass Completed', placeholder: 'e.g. Full bleed, 3 passes' },
  { key: 'nitrogen', label: 'Nitrogen / Air Charge', placeholder: 'e.g. 200 psi nitrogen' },
]

const ADDITIONAL_SERVICE_INFO_FIELDS = [
  { key: 'aircan_grease', label: 'Air Can Grease / Lubricant', placeholder: 'e.g. Slick Honey, Sram Butter' },
  { key: 'mount_hw', label: 'Mount Hardware Size', placeholder: 'e.g. 8mm eyelet' },
  { key: 'damp_oil', label: 'Damping Oil Used', placeholder: 'e.g. Fox Float Fluid 20wt' },
  { key: 'bleed', label: 'Bleed Method / Pass Completed', placeholder: 'e.g. Full bleed, 3 passes' },
  { key: 'seal_torque', label: 'Seal Head Torque (Nm)' },
  { key: 'ifp_depth', label: 'IFP Depth (mm)' },
  { key: 'ifp_psi', label: 'IFP Pressure (psi)' },
  { key: 'nitrogen', label: 'Nitrogen / Air Charge', placeholder: 'e.g. 200 psi nitrogen' },
  { key: 'shim_torque', label: 'Shim Stack Torque (Nm)' },
]

const SERVICE_KIT_FIELD_KEYS = new Set(['kit', 'spring_hw'])

const INSPECTION_FIELDS = [
  ['Stanchion / shaft condition OK', 'ins_stanchion'],
  ['CSU / crown / steerer OK', 'ins_csu'],
  ['Bushing play acceptable', 'ins_bushing_play'],
  ['Air shaft wear acceptable', 'ins_air_shaft'],
  ['Damper shaft wear acceptable', 'ins_damp_shaft'],
  ['Eyelet / hardware wear OK', 'ins_eyelet'],
  ['Bearing mount condition OK', 'ins_bearing'],
  ['Wiper / foam ring OK', 'ins_wiper'],
  ['Oil contamination noted', 'ins_oil_contam'],
  ['Internal scoring / damage noted', 'ins_scoring'],
  ['Adjusters functioning correctly', 'ins_adjusters'],
  ['Lockout / threshold functioning', 'ins_lockout'],
]

const QC_FIELDS = [
  ['Correct torque settings confirmed', 'qc_torque'],
  ['Correct pressures / spring installed', 'qc_pressure'],
  ['Correct clickers reset', 'qc_clickers'],
  ['Full travel smooth', 'qc_travel'],
  ['No topout / knock', 'qc_topout'],
  ['No oil leaks', 'qc_oil_leak'],
  ['No air loss detected', 'qc_air_loss'],
  ['All adjusters functional', 'qc_adjusters'],
  ['Returned to customer baseline', 'qc_baseline'],
  ['FLI sticker applied', 'qc_sticker'],
]

const HANDOVER_FIELDS = ['interval','next_service','next_service_date','further_work','worn_parts','setup_rec','notes']

const DEFAULT = {
  customer_id:'', component_id:'', bike_id:'', date: new Date().toISOString().split('T')[0], scheduled_date:'', due_date:'',
  status:'received', technician:'', technician_id:'', scheduled_duration_mins:'', start_time:'', finish_time:'', duration_mins:0,
  physical_location:'',
  structured_measurements:{},
  service_template_id:'', service_template_snapshot:null, service_template_addon_ids:[], service_template_addon_snapshots:[],
  sc_lower_leg:false, sc_air_spring:false, sc_damper:false, sc_air_can:false,
  sc_full_shock:false, sc_bushing:false, sc_travel:false, sc_seal_upgrade:false,
  sc_retune:false, sc_setup:false, sc_dropper_service:false,
  b_air:'', b_sag:'', b_preload:'', b_tokens:'', b_lsc:'', b_hsc:'', b_pedal:'', b_hbo:'', b_lsr:'', b_hsr:'',
  a_air:'', a_sag:'', a_preload:'', a_tokens:'', a_lsc:'', a_hsc:'', a_pedal:'', a_hbo:'', a_lsr:'', a_hsr:'',
  shim_torque:'', ifp_depth:'', ifp_psi:'', tokens:'', oil_l:'', oil_r:'',
  damp_vol:'', seal_torque:'', topcap_torque:'', shaft_torque:'',
  travel_config:'', mount_hw:'', damp_oil:'', aircan_grease:'',
  kit:'', spring_hw:'', bleed:'', nitrogen:'',
  ins_stanchion:false, ins_csu:false, ins_bushing_play:false, ins_air_shaft:false,
  ins_damp_shaft:false, ins_eyelet:false, ins_bearing:false, ins_wiper:false,
  ins_oil_contam:false, ins_scoring:false, ins_adjusters:false, ins_lockout:false,
  condition_notes:'',
  qc_torque:false, qc_pressure:false, qc_clickers:false, qc_travel:false,
  qc_topout:false, qc_oil_leak:false, qc_air_loss:false, qc_adjusters:false,
  qc_baseline:false, qc_sticker:false,
  qc_status:'', qc_signed_off_at:'', qc_signed_off_by:'', qc_signed_off_by_name:'', qc_notes:'',
  interval:'', next_service:'', next_service_date:'', reminder_text:'', further_work:'', worn_parts:'', setup_rec:'',
  inspection_completed_at:'', inspection_summary:'',
  estimate_amount:'', estimate_parts_cost:'', estimate_notes:'', estimate_sent_at:'',
  customer_decision:'', customer_decision_at:'', customer_decision_note:'',
  quote_last_sent_at:'', report_sent_at:'',
  parts_used:[],
  parts_cost:'', labour_charge:'', total_charged:'', notes:''
}

const DEFAULT_ADJUSTMENT_FORM = {
  kind: 'discount',
  value_type: 'fixed',
  value: '',
  target_scope: 'selected_service_lines',
  target_line_ids: [],
  label: '',
  reason: '',
}

function hasProtectedDeleteMarkers(job = {}) {
  return job.payment_status === 'paid'
    || Boolean(job.payment_paid_at)
    || Boolean(job.payment_link)
    || Boolean(job.stripe_checkout_session_id)
    || Boolean(job.stripe_payment_intent_id)
    || Boolean(job.stripe_payment_link_id)
    || Boolean(job.payment_reference)
    || Boolean(job.invoice_number)
    || Boolean(job.report_sent_at)
}

async function hasProtectedDeleteHistory(jobId) {
  if (!jobId) return false
  const checks = [
    supabase.from('job_report_versions').select('id', { count: 'exact', head: true }).eq('job_id', jobId),
    supabase.from('audit_events').select('id', { count: 'exact', head: true }).eq('entity_type', 'job').eq('entity_id', jobId),
    supabase.from('notification_log').select('id', { count: 'exact', head: true }).eq('job_id', jobId),
  ]
  const results = await Promise.all(checks)
  const failed = results.find(result => result.error)
  if (failed) throw failed.error
  return results.some(result => (result.count || 0) > 0)
}

const PROTECTED_DELETE_MESSAGE = 'This service record has payment, invoice, report, or audit history and cannot be hard-deleted. Set the job status to Cancelled and save instead. This does not issue a refund or reverse any payment.'

function suspensionServiceSnapshot(service = {}) {
  return {
    id: service.id,
    module_key: 'suspension',
    category: service.category || '',
    category_label: service.category || '',
    name: service.name || '',
    description: service.description || '',
    brand: service.brand || '',
    is_addon: !!service.is_addon,
    base_price: Number(service.base_price || 0),
    expected_labour_minutes: Number(service.expected_labour_minutes || 0),
  }
}

function buildInitialForm(data, preCustomerId, preComponentId) {
  if (!data) return { ...DEFAULT, customer_id: preCustomerId || '', component_id: preComponentId || '' }
  const f = { ...DEFAULT }
  Object.keys(f).forEach(k => {
    if (data[k] !== undefined && data[k] !== null) {
      if (k === 'service_template_snapshot') {
        f[k] = normaliseTemplateSnapshot(data[k])
      } else if (k === 'service_template_addon_ids' || k === 'service_template_addon_snapshots') {
        f[k] = normaliseTemplateArray(data[k])
      } else if (k === 'structured_measurements') {
        f[k] = normaliseObject(data[k])
      } else if (k === 'parts_used') {
        f[k] = normaliseTemplateArray(data[k])
      } else {
        f[k] = BOOL_FIELDS.includes(k) ? !!data[k] : (data[k] === 0 && ['estimate_amount','estimate_parts_cost','parts_cost','labour_charge','total_charged'].includes(k) ? '' : String(data[k] ?? ''))
      }
    }
  })
  f.duration_mins = data.duration_mins || 0
  f.scheduled_duration_mins = data.scheduled_duration_mins ?? ''
  return f
}

function normaliseTemplateSnapshot(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function normaliseTemplateArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function normaliseObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function mergeTemplateDefinitions(liveTemplate, snapshot) {
  if (!liveTemplate && !snapshot) return null
  if (!liveTemplate) return snapshot
  if (!snapshot) return liveTemplate

  const merged = { ...liveTemplate, ...snapshot }
  ;[
    'scope_fields',
    'scope_options',
    'inspection_fields',
    'qc_fields',
    'visible_settings_fields',
    'visible_service_log_fields',
    'visible_inspection_checks',
    'visible_qc_checks',
    'visible_handover_fields',
    'hidden_fields',
    'optional_collapsed_fields',
    'available_addons',
    'default_addons',
  ].forEach(key => {
    const snapshotValue = normaliseTemplateArray(snapshot[key])
    const liveValue = normaliseTemplateArray(liveTemplate[key])
    merged[key] = snapshotValue.length ? snapshotValue : liveValue
  })

  const snapshotMeasurements = normaliseMeasurementSections(snapshot.measurement_schema)
  const liveMeasurements = normaliseMeasurementSections(liveTemplate.measurement_schema)
  const liveSettings = liveMeasurements.filter(section => section.category === 'settings')
  if (snapshotMeasurements.length && liveSettings.length) {
    const snapshotNonSettings = snapshotMeasurements.filter(section => section.category !== 'settings')
    const snapshotKeys = new Set(snapshotNonSettings.map(section => section.key))
    const liveNonSettings = liveMeasurements.filter(section => section.category !== 'settings' && !snapshotKeys.has(section.key))
    merged.measurement_schema = [...liveSettings, ...snapshotNonSettings, ...liveNonSettings]
  } else {
    merged.measurement_schema = snapshotMeasurements.length ? snapshotMeasurements : liveMeasurements
  }
  merged.recommended_defaults = {
    ...(liveTemplate.recommended_defaults || {}),
    ...(snapshot.recommended_defaults || {}),
  }
  return merged
}

function normaliseMeasurementSections(value) {
  return normaliseTemplateArray(value)
    .filter(section => section && typeof section === 'object')
    .map(section => ({
      key: section.key || section.title || 'measurement_section',
      title: section.title || 'Measurements',
      description: section.description || '',
      category: section.category || 'service_log',
      fields: normaliseTemplateArray(section.fields).filter(field => field && typeof field === 'object'),
    }))
    .filter(section => section.fields.length > 0)
}

function uniqueValues(values = []) {
  return Array.from(new Set((values || []).filter(Boolean)))
}

function arrayFromTemplate(template, key, fallbackKey = null) {
  if (!template) return []
  const primary = normaliseTemplateArray(template[key])
  if (primary.length || !fallbackKey) return primary
  return normaliseTemplateArray(template[fallbackKey])
}

function isAddonTemplate(template) {
  return Boolean(template?.is_addon || template?.template_kind === 'addon' || template?.service_category === 'fork_upgrade_addon')
}

function chunks(items, size = 4) {
  const result = []
  for (let i = 0; i < items.length; i += size) result.push(items.slice(i, i + size))
  return result
}

const sh = (label, color) => (
  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color, marginBottom: 8, marginTop: 14 }}>{label}</div>
)

const sectionPanelStyle = {
  background: 'var(--black)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: 14,
  marginBottom: 16,
}

const SUSPENSION_SECTION_HELP = {
  scope: 'Work and labour sold for this suspension job.',
  settings: 'Before and after setup values for the serviced component.',
  inspection: 'Condition checks and workshop observations.',
  serviceLog: 'Suspension-only kit recommendation and service kit record.',
  quote: 'One-off work outside the standard service menu.',
  evidence: 'Photos, videos and files for this suspension job.',
  qc: 'Final safety and quality sign-off.',
  handover: 'Customer notes, service interval and follow-up advice.',
  stockMaterials: 'Physical parts, stock and materials fitted or supplied.',
  financials: 'Charges, discounts, adjustments and totals.',
  notes: 'Private workshop notes.',
}

const SUSPENSION_WORKSPACE_TABS = [
  ['work', 'Work'],
  ['setup', 'Setup & findings'],
  ['parts', 'Parts & evidence'],
  ['qc', 'QC & handover'],
  ['admin', 'Pricing & admin'],
]

const SUSPENSION_SECTION_TABS = {
  scope: 'work',
  serviceLog: 'work',
  extraServices: 'work',
  settings: 'setup',
  inspection: 'setup',
  evidence: 'parts',
  stockMaterials: 'parts',
  qc: 'qc',
  handover: 'qc',
  quote: 'admin',
  financials: 'admin',
  notes: 'admin',
}

function kitContextText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
}

function kitContextCode(value) {
  return String(value || '').trim().toUpperCase()
}

function tokenOverlapScore(a, b) {
  const aTokens = new Set(kitContextText(a).split(/[^a-z0-9]+/).filter(token => token.length > 1))
  const bTokens = new Set(kitContextText(b).split(/[^a-z0-9]+/).filter(token => token.length > 1))
  if (!aTokens.size || !bTokens.size) return 0

  let matches = 0
  aTokens.forEach(token => {
    if (bTokens.has(token)) matches += 1
  })
  return matches * 6
}

function kitFactScore(fact, context) {
  let score = 0
  const brand = kitContextText(fact.component_brand)
  const type = kitContextText(fact.component_type)
  const model = kitContextText(fact.component_model)
  const year = kitContextText(fact.component_year)
  const travel = kitContextText(fact.component_travel)
  const partNumber = kitContextCode(fact.component_part_number)
  const modelCode = kitContextCode(fact.component_model_code)

  if (context.brand && brand === context.brand) score += 30
  if (context.type && type === context.type) score += 25
  if (context.modelCode && modelCode === context.modelCode) score += 90
  if (context.partNumber && partNumber === context.partNumber) score += 70

  if (context.model && model) {
    if (model === context.model) score += 50
    else if (model.includes(context.model) || context.model.includes(model)) score += 35
    else score += tokenOverlapScore(model, context.model)
  }

  if (context.year && year === context.year) score += 6
  if (context.travel && travel === context.travel) score += 6
  if (context.templateId && fact.service_template_id === context.templateId) score += 15

  score += Math.min(Number(fact.usage_count || 0) * 4, 28)
  score += Math.min(Number(fact.workshop_count || 0) * 8, 32)
  return score
}

function SuspensionJobModal({ workshopId, customers, components, services = [], preCustomerId, preComponentId, onClose, onSaved, onChanged, onDeleted, initialData, returnLabel = 'Back' }) {
  const navigate = useNavigate()
  const { profile, user, workshop } = useAuth()
  const isEdit = !!initialData
  const inheritedServicePrices = isEdit && normaliseJobServiceSnapshot(initialData?.service_snapshot).some(line => line?.vat_source === true)
  const shouldShowTargetSelectors = !isEdit ? (!preCustomerId || !preComponentId) : !initialData?.component_id
  const draftKey = `rebound_job_draft_${workshopId}_${initialData?.id || 'new'}`

  const [form, setForm] = useState(() => {
    // For new jobs, check for a saved draft first
    if (!initialData) {
      try {
        const saved = localStorage.getItem(draftKey)
        if (saved) {
          const parsed = JSON.parse(saved)
          return { ...buildInitialForm(null, preCustomerId, preComponentId), ...parsed }
        }
      } catch {}
    }
    return buildInitialForm(initialData, preCustomerId, preComponentId)
  })
  const [saving, setSaving] = useState(false)
  const [savedForm, setSavedForm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(null)
  const [hasDraft, setHasDraft] = useState(() => {
    if (initialData) return false
    try { return !!localStorage.getItem(`rebound_job_draft_${workshopId}_new`) } catch { return false }
  })
  const [duration, setDuration] = useState(null)
  const [selectedServices, setSelectedServices] = useState(() =>
    Array.isArray(initialData?.service_ids) ? initialData.service_ids : []
  )
  const vatIntake = useVatIntakePrices(workshopId, 'suspension', selectedServices, !isEdit)
  const suspensionServiceRows = services.filter(isSuspensionService)
  const [jobMeta, setJobMeta] = useState(initialData || {})
  const [priceAdjustments, setPriceAdjustments] = useState(() => normalisePriceAdjustments(initialData || {}))
  const [extraServiceLineRows, setExtraServiceLineRows] = useState(() => initialData?.job_extra_service_line_requests || [])
  const [adjustmentForm, setAdjustmentForm] = useState(DEFAULT_ADJUSTMENT_FORM)
  const [adjustmentSaving, setAdjustmentSaving] = useState(false)
  const [adjustmentNotice, setAdjustmentNotice] = useState('')
  const [adjustmentError, setAdjustmentError] = useState('')
  const [loyaltyJobs, setLoyaltyJobs] = useState([])
  const [loyaltyRewards, setLoyaltyRewards] = useState([])
  const [loyaltyBusy, setLoyaltyBusy] = useState(false)
  const [loyaltyWorkshop, setLoyaltyWorkshop] = useState(workshop || {})
  const [teamMembers, setTeamMembers] = useState([])
  const [serviceMenuOpen, setServiceMenuOpen] = useState(() => !initialData?.service_ids?.length)
  const [workflowMode, setWorkflowMode] = useState(null)
  const [workflowSaving, setWorkflowSaving] = useState(false)
  const [workflowNotice, setWorkflowNotice] = useState('')
  const [workflowError, setWorkflowError] = useState('')
  const [workspaceTab, setWorkspaceTab] = useState('work')
  const [visitedWorkspaceTabs, setVisitedWorkspaceTabs] = useState(() => new Set(['work']))
  const [scheduleOverrideOverCapacity, setScheduleOverrideOverCapacity] = useState(false)
  const [customerNavSaving, setCustomerNavSaving] = useState(false)
  const [quoteEmail, setQuoteEmail] = useState('')
  const [serviceTemplates, setServiceTemplates] = useState([])
  const [templateChoice, setTemplateChoice] = useState('')
  const [templateNotice, setTemplateNotice] = useState('')
  const [templateDetailsOpen, setTemplateDetailsOpen] = useState(() =>
    !(initialData?.service_template_id || initialData?.service_template_snapshot)
  )
  const [openSections, setOpenSections] = useState(() => ({
    scope: !initialData?.service_template_snapshot,
    settings: false,
    inspection: !initialData?.inspection_completed_at,
    serviceLog: true,
    extraServices: Boolean(initialData?.job_extra_service_line_requests?.length),
    quote: Boolean(initialData?.estimate_amount || initialData?.estimate_sent_at || initialData?.customer_decision === 'pending'),
    evidence: false,
    qc: Boolean(initialData?.inspection_completed_at && !initialData?.qc_signed_off_at),
    handover: false,
    stockMaterials: false,
    financials: false,
    notes: false,
  }))

  useEffect(() => {
    setJobMeta(initialData || {})
    setPriceAdjustments(normalisePriceAdjustments(initialData || {}))
    setExtraServiceLineRows(initialData?.job_extra_service_line_requests || [])
  }, [initialData])

  // Kit library
  const [kitRecs, setKitRecs] = useState([])       // recommendations from shared library
  const [savingKit, setSavingKit] = useState(false)
  const [showAddKit, setShowAddKit] = useState(false)
  const [newKit, setNewKit] = useState({ part_number: '', description: '', unit_cost: '', supplier: '' })
  const [trailheadKitState, setTrailheadKitState] = useState(null)
  const [trailheadKitMsg, setTrailheadKitMsg] = useState('')

  const selectedComponent = components.find(c => c.id === form.component_id)
  const modelCode = selectedComponent?.sram_model_code?.toUpperCase() || selectedComponent?.part_number?.toUpperCase() || ''
  const kitContextLabel = modelCode || [selectedComponent?.brand, selectedComponent?.type].filter(Boolean).join(' ') || 'this component'
  const trailheadKitEligible = String(selectedComponent?.brand || '').toLowerCase() === 'rockshox'
    && ['fork', 'shock', 'dropper'].includes(String(selectedComponent?.type || '').toLowerCase())

  const effectiveLoyaltyWorkshop = { ...(workshop || {}), ...(loyaltyWorkshop || {}) }

  const loadLoyalty = useCallback(async () => {
    if (!workshopId) return
    const wsRes = await supabase
      .from('workshops')
      .select(LOYALTY_WORKSHOP_COLUMNS)
      .eq('id', workshopId)
      .single()
    setLoyaltyWorkshop({ ...(workshop || {}), ...(wsRes?.data || {}) })
    const enabled = wsRes?.data?.loyalty_enabled === true
    if (!enabled || !form.customer_id) {
      setLoyaltyJobs([])
      setLoyaltyRewards([])
      return
    }
    const [jobsRes, rewardsRes] = await Promise.all([
      supabase
        .from('jobs')
        .select(JOB_FINANCIAL_SELECT)
        .eq('workshop_id', workshopId)
        .eq('customer_id', form.customer_id),
      supabase
        .from('loyalty_rewards')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('customer_id', form.customer_id)
        .order('created_at', { ascending: false }),
    ])
    setLoyaltyJobs(jobsRes.data || [])
    setLoyaltyRewards(rewardsRes.data || [])
  }, [form.customer_id, workshop, workshopId])

  useEffect(() => { loadLoyalty() }, [loadLoyalty])

  function currentKitContext() {
    const template = activeTemplate || normaliseTemplateSnapshot(form.service_template_snapshot) || {}
    const selectedServiceRows = suspensionServiceRows.filter(s => selectedServices.includes(s.id))
    const serviceSummary = selectedServiceRows.map(s => s.name).join(', ') || form.service_summary || initialData?.service_summary || ''

    return {
      brand: kitContextText(selectedComponent?.brand),
      type: kitContextText(selectedComponent?.type),
      model: kitContextText(selectedComponent?.model),
      year: kitContextText(selectedComponent?.year),
      travel: kitContextText(selectedComponent?.travel || selectedComponent?.stroke || selectedComponent?.travel_stroke),
      partNumber: kitContextCode(selectedComponent?.part_number),
      modelCode: kitContextCode(modelCode),
      templateId: template.id || form.service_template_id || null,
      templateName: template.name || '',
      serviceCategory: template.service_category || '',
      serviceLevel: template.service_level || '',
      serviceSummary,
    }
  }

  function kitSearchText(kit) {
    return [
      kit.model_code,
      kit.model_name,
      kit.part_number,
      kit.description,
    ].filter(Boolean).join(' ').toLowerCase()
  }

  function generalKitMatchesComponent(kit) {
    if (kit.model_code) return true
    if (kit.learned_score) return true
    if (!selectedComponent) return true

    const text = kitSearchText(kit)
    const brand = String(selectedComponent.brand || '').toLowerCase()
    const type = String(selectedComponent.type || '').toLowerCase()
    const brandAliases = brand === 'rockshox' ? ['rockshox', 'sram'] : brand ? [brand] : []

    if (brandAliases.length && !brandAliases.some(alias => text.includes(alias))) return false
    if (type === 'fork') return text.includes('fork')
    if (type === 'shock') return text.includes('shock')
    if (type === 'dropper') return text.includes('dropper') || text.includes('seatpost') || text.includes('transfer') || text.includes('reverb')
    return true
  }

  function sortKitRecommendations(kits, code = modelCode) {
    return (kits || []).filter(generalKitMatchesComponent).sort((a, b) => {
      if (a.model_code === code && b.model_code !== code) return -1
      if (b.model_code === code && a.model_code !== code) return 1
      const learnedDiff = Number(b.learned_score || 0) - Number(a.learned_score || 0)
      if (learnedDiff) return learnedDiff
      const usageDiff = Number(b.learned_usage_count || 0) - Number(a.learned_usage_count || 0)
      if (usageDiff) return usageDiff
      return String(a.part_number || '').localeCompare(String(b.part_number || ''))
    })
  }

  function mergeKitRecommendations(baseKits = [], learnedKitMap = new Map(), code = modelCode) {
    const merged = new Map()
    baseKits.forEach(kit => {
      const learned = learnedKitMap.get(kit.id)
      merged.set(kit.id, learned ? { ...kit, ...learned } : kit)
    })
    learnedKitMap.forEach((kit, id) => {
      if (!merged.has(id)) merged.set(id, kit)
    })
    return sortKitRecommendations(Array.from(merged.values()), code)
  }

  function renderKitRecommendationMeta(kit) {
    const usageCount = Number(kit.learned_usage_count || 0)
    if (!usageCount) return null

    const workshopCount = Number(kit.learned_workshop_count || 0)
    return (
      <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 2 }}>
        Recommended from {usageCount} previous use{usageCount === 1 ? '' : 's'}
        {workshopCount > 1 ? ` across ${workshopCount} workshops` : ''}
      </div>
    )
  }

  async function loadLearnedKitRecommendations() {
    if (!selectedComponent) return new Map()

    const context = currentKitContext()
    if (!context.brand && !context.type) return new Map()

    try {
      let query = supabase
        .from('kit_recommendation_facts')
        .select(`
          kit_id,
          component_brand, component_type, component_model, component_year,
          component_travel, component_part_number, component_model_code,
          service_template_id, service_template_name, service_category, service_level,
          kit_part_number, kit_description, usage_count, workshop_count, last_used_at
        `)
        .limit(250)

      if (context.brand) query = query.eq('component_brand', context.brand)
      if (context.type) query = query.eq('component_type', context.type)

      const { data: facts, error } = await query
      if (error) return new Map()

      const learnedFacts = new Map()
      ;(facts || []).forEach(fact => {
        const score = kitFactScore(fact, context)
        if (!score) return

        const current = learnedFacts.get(fact.kit_id) || {
          score: 0,
          usageCount: 0,
          workshopCount: 0,
          lastUsedAt: null,
        }
        learnedFacts.set(fact.kit_id, {
          score: Math.max(current.score, score),
          usageCount: current.usageCount + Number(fact.usage_count || 0),
          workshopCount: Math.max(current.workshopCount, Number(fact.workshop_count || 0)),
          lastUsedAt: !current.lastUsedAt || new Date(fact.last_used_at) > new Date(current.lastUsedAt)
            ? fact.last_used_at
            : current.lastUsedAt,
        })
      })

      const ids = Array.from(learnedFacts.entries())
        .sort((a, b) => b[1].score - a[1].score)
        .slice(0, 30)
        .map(([id]) => id)

      if (!ids.length) return new Map()

      const { data: kits, error: kitError } = await supabase
        .from('kit_library')
        .select(`
          id, model_code, model_name, part_number, description,
          kit_prices(unit_cost, supplier, updated_at)
        `)
        .in('id', ids)

      if (kitError) return new Map()

      return new Map((kits || []).map(kit => {
        const learned = learnedFacts.get(kit.id) || {}
        return [kit.id, {
          ...kit,
          learned_score: learned.score || 0,
          learned_usage_count: learned.usageCount || 0,
          learned_workshop_count: learned.workshopCount || 0,
          learned_last_used_at: learned.lastUsedAt || null,
        }]
      }))
    } catch {
      return new Map()
    }
  }

  async function recordKitUsage(kit) {
    if (!kit?.id || !workshopId || !selectedComponent || !initialData?.id) return

    const context = currentKitContext()
    const payload = {
      kit_id: kit.id,
      workshop_id: workshopId,
      job_id: initialData.id,
      component_id: selectedComponent.id || null,
      component_brand: context.brand,
      component_type: context.type,
      component_model: context.model,
      component_year: context.year,
      component_travel: context.travel,
      component_part_number: context.partNumber,
      component_model_code: context.modelCode,
      service_template_id: context.templateId || null,
      service_template_name: context.templateName,
      service_category: context.serviceCategory,
      service_level: context.serviceLevel,
      service_summary: context.serviceSummary,
      kit_part_number: kitContextCode(kit.part_number),
      kit_description: String(kit.description || '').trim(),
      used_by: profile?.id || user?.id || null,
      updated_at: new Date().toISOString(),
    }

    try {
      await supabase
        .from('kit_usage_signals')
        .upsert(payload, { onConflict: 'job_id,kit_id' })
    } catch {}
  }

  useEffect(() => {
    let cancelled = false

    async function loadTeamMembers() {
      if (!workshopId) {
        setTeamMembers([])
        return
      }

      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role, weekly_bookable_hours, calendar_colour')
        .eq('workshop_id', workshopId)
        .order('full_name')

      if (cancelled) return

      const seen = new Set()
      const members = (data || [])
        .map(member => ({
          ...member,
          name: String(member.full_name || '').trim(),
        }))
        .filter(member => member.name)
        .filter(member => {
          const key = member.name.toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

      setTeamMembers(members)
    }

    loadTeamMembers()
    return () => { cancelled = true }
  }, [workshopId])

  useEffect(() => {
    let cancelled = false

    async function loadTemplates() {
      if (!workshopId) {
        setServiceTemplates([])
        return
      }

      const { data, error } = await supabase
        .from('service_templates')
        .select('*')
        .or(`workshop_id.is.null,workshop_id.eq.${workshopId}`)
        .eq('active', true)
        .order('component_type')
        .order('sort_order')

      if (cancelled) return
      if (error) {
        setServiceTemplates([])
        return
      }

      setServiceTemplates(data || [])
    }

    loadTemplates()
    return () => { cancelled = true }
  }, [workshopId])

  // Load kit recommendations when component changes
  useEffect(() => {
    let cancelled = false

    async function loadKits() {
      const lookupCodes = modelCode ? [modelCode, ''] : ['']
      const [{ data: kits }, learnedKits] = await Promise.all([
        supabase
          .from('kit_library')
          .select(`
            id, model_code, model_name, part_number, description,
            kit_prices(unit_cost, supplier, updated_at)
          `)
          .in('model_code', lookupCodes)
          .order('created_at'),
        loadLearnedKitRecommendations(),
      ])
      if (cancelled) return
      setKitRecs(mergeKitRecommendations(kits || [], learnedKits))
      setTrailheadKitState(null)
      setTrailheadKitMsg('')
    }
    loadKits()
    return () => { cancelled = true }
  }, [
    modelCode,
    selectedComponent?.brand,
    selectedComponent?.type,
    selectedComponent?.model,
    selectedComponent?.year,
    selectedComponent?.travel,
    selectedComponent?.stroke,
    form.service_template_id,
  ])

  async function applyKit(kit) {
    applyKitToForm(kit)
    await recordKitUsage(kit)
  }

  async function saveTrailheadKitRecommendations(data) {
    const serviceKits = data?.serviceKits || []
    const mc = (modelCode || data?.modelCode || '').toUpperCase().trim()
    if (!workshopId || !mc || serviceKits.length === 0) return []

    const modelName = selectedComponent
      ? `${selectedComponent.year ? selectedComponent.year + ' ' : ''}${selectedComponent.model}`
      : (data.productName || mc)
    const savedKits = []

    for (const kit of serviceKits) {
      const partNumber = String(kit.partNumber || '').toUpperCase().trim()
      const description = String(kit.description || kit.name || '').trim()
      if (!partNumber || !description) continue

      const { data: existing } = await supabase
        .from('kit_library')
        .select('id, model_code, model_name, part_number, description, created_at, kit_prices(unit_cost, supplier, updated_at)')
        .eq('model_code', mc)
        .eq('part_number', partNumber)
        .maybeSingle()

      if (existing?.id) {
        savedKits.push(existing)
        continue
      }

      const { data: inserted } = await supabase
        .from('kit_library')
        .insert({
          model_code: mc,
          model_name: modelName,
          part_number: partNumber,
          description,
          contributed_by: workshopId,
        })
        .select('id, model_code, model_name, part_number, description, created_at, kit_prices(unit_cost, supplier, updated_at)')
        .single()

      if (inserted) savedKits.push(inserted)
    }

    return savedKits
  }

  async function findTrailheadServiceKit() {
    const serial = String(selectedComponent?.serial || '').trim()
    if (!serial) {
      setTrailheadKitState('error')
      setTrailheadKitMsg('Add the RockShox serial number to the component first.')
      return
    }

    setTrailheadKitState('loading')
    setTrailheadKitMsg('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const res = await fetch('/.netlify/functions/rockshox-trailhead-lookup?serial=' + encodeURIComponent(serial), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const data = await res.json()

      if (!res.ok || !data.found) {
        setTrailheadKitState('error')
        setTrailheadKitMsg(data.error || 'Trailhead did not return this component.')
        return
      }

      if (!data.serviceKits?.length) {
        setTrailheadKitState('error')
        setTrailheadKitMsg('Trailhead did not list a service kit for this serial.')
        return
      }

      const savedKits = await saveTrailheadKitRecommendations(data)
      if (savedKits.length === 0) {
        setTrailheadKitState('error')
        setTrailheadKitMsg('Trailhead found a kit, but Rebound could not save it to the library.')
        return
      }

      setKitRecs(savedKits)
      setTrailheadKitState('found')
      setTrailheadKitMsg('Trailhead service kit found and saved to the kit library.')
    } catch {
      setTrailheadKitState('error')
      setTrailheadKitMsg('Trailhead kit lookup failed. Add the kit manually for now.')
    }
  }

  async function saveNewKit() {
    if (!newKit.part_number || !newKit.description) return
    setSavingKit(true)
    const mc = modelCode || ''
    const modelName = selectedComponent
      ? `${selectedComponent.year ? selectedComponent.year + ' ' : ''}${selectedComponent.model}`
      : mc

    // Upsert into shared library
    const { data: existing } = await supabase
      .from('kit_library')
      .select('id')
      .eq('model_code', mc)
      .eq('part_number', newKit.part_number.toUpperCase().trim())
      .single()

    let kitId = existing?.id

    if (!kitId) {
      const { data: inserted } = await supabase
        .from('kit_library')
        .insert({
          model_code: mc,
          model_name: modelName,
          part_number: newKit.part_number.toUpperCase().trim(),
          description: newKit.description.trim(),
          contributed_by: workshopId,
        })
        .select('id')
        .single()
      kitId = inserted?.id
    }

    // Upsert workshop price
    if (kitId && newKit.unit_cost) {
      await supabase
        .from('kit_prices')
        .upsert({
          kit_id: kitId,
          workshop_id: workshopId,
          unit_cost: Number(newKit.unit_cost),
          supplier: newKit.supplier.trim(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'kit_id,workshop_id' })
    }

    const appliedKit = {
      id: kitId,
      part_number: newKit.part_number.toUpperCase().trim(),
      description: newKit.description.trim(),
      kit_prices: newKit.unit_cost ? [{
        unit_cost: Number(newKit.unit_cost),
        supplier: newKit.supplier.trim(),
      }] : [],
    }

    applyKitToForm(appliedKit)
    await recordKitUsage(appliedKit)

    // Reload recs
    const lookupCodes = mc ? [mc, ''] : ['']
    const [{ data: kits }, learnedKits] = await Promise.all([
      supabase
        .from('kit_library')
        .select('id, model_code, model_name, part_number, description, kit_prices(unit_cost, supplier, updated_at)')
        .in('model_code', lookupCodes)
        .order('created_at'),
      loadLearnedKitRecommendations(),
    ])
    setKitRecs(mergeKitRecommendations(kits || [], learnedKits, mc))
    setShowAddKit(false)
    setNewKit({ part_number: '', description: '', unit_cost: '', supplier: '' })
    setSavingKit(false)
  }

  const custComponents = components.filter(c => c.customer_id === form.customer_id)

  useEffect(() => {
    if (form.start_time && form.finish_time) {
      const m = calcMins(form.start_time, form.finish_time)
      setDuration(m > 0 ? m : null)
      setForm(f => ({ ...f, duration_mins: m > 0 ? m : 0 }))
    } else {
      setDuration(null)
    }
  }, [form.start_time, form.finish_time])

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), [])
  const setChk = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), [])

  function editablePartsUsed(value) {
    if (Array.isArray(value)) return value
    try {
      const parsed = JSON.parse(value || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  function withPartsUsedTotals(values, rows) {
    const cleanRows = normaliseJobPartsUsed(rows)
    const totals = jobPartsTotals(cleanRows)
    const hadStructuredRows = editablePartsUsed(values.parts_used).length > 0
    const next = {
      ...values,
      parts_used: rows,
      parts_cost: cleanRows.length ? totals.costTotal.toFixed(2) : hadStructuredRows ? '' : values.parts_cost,
    }
    return {
      ...next,
      total_charged: totalFor(next).toFixed(2),
    }
  }

  function kitDisplayValue(kit = {}) {
    return [kit.part_number, kit.description]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' — ')
  }

  function buildPartRow(seed = {}) {
    return {
      id: seed.id || `part-${Date.now()}`,
      name: seed.name || '',
      part_number: seed.part_number || '',
      supplier: seed.supplier || '',
      quantity: seed.quantity || '1',
      unit_cost: seed.unit_cost || '',
      unit_price: seed.unit_price || '',
    }
  }

  function addPartRow(seed = {}) {
    setForm(f => {
      const rows = mergeJobPartRows(editablePartsUsed(f.parts_used), buildPartRow(seed))
      return withPartsUsedTotals(f, rows)
    })
  }

  function applyKitToForm(kit) {
    const kitValue = kitDisplayValue(kit)
    const kitRow = buildPartRow(partRowFromServiceKit(kit))
    setForm(f => {
      const rows = mergeJobPartRows(editablePartsUsed(f.parts_used), kitRow, { replaceMatching: true })
      return withPartsUsedTotals({ ...f, kit: kitValue || f.kit }, rows)
    })
  }

  function updatePartRow(index, key, value) {
    setForm(f => {
      const rows = editablePartsUsed(f.parts_used).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row
      )
      return withPartsUsedTotals(f, rows)
    })
  }

  function removePartRow(index) {
    setForm(f => {
      const currentRows = editablePartsUsed(f.parts_used)
      const removedRow = currentRows[index]
      const rows = currentRows.filter((_, rowIndex) => rowIndex !== index)
      const nextValues = isServiceKitPartRow(removedRow) && !rows.some(isServiceKitPartRow)
        ? { ...f, kit: '' }
        : f
      return withPartsUsedTotals(nextValues, rows)
    })
  }

  // Auto-save draft to localStorage on every form change (new jobs only)
  useEffect(() => {
    if (isEdit) return
    try {
      localStorage.setItem(draftKey, JSON.stringify(form))
    } catch {}
  }, [form, isEdit, draftKey])

  function clearDraft() {
    try { localStorage.removeItem(draftKey) } catch {}
  }

  const financials = jobFinancials({
    ...jobMeta,
    ...form,
    job_price_adjustments: priceAdjustments,
    job_extra_service_line_requests: extraServiceLineRows,
  })
  const displayedTotal = vatIntake.gross ?? financials.totalCost
  const partRows = editablePartsUsed(form.parts_used)
  const stockUsageRows = Array.isArray(jobMeta.job_stock_usage) ? jobMeta.job_stock_usage : []
  const partsCostTotal = Number(financials.basePartsCost || 0)
  const partsSoldTotal = Number(financials.partsSellTotal || 0)
  const partsMarginTotal = partsSoldTotal - partsCostTotal
  function kitIsApplied(kit = {}) {
    const kitRowId = partRowFromServiceKit(kit).id
    const kitValue = kitDisplayValue(kit)
    return partRows.some(row => row.id === kitRowId) || (kitValue && String(form.kit || '').trim() === kitValue)
  }
  const legacyPartsCost = Number(form.parts_cost || 0)
  const quoteStatusLabel = form.customer_decision === 'approved'
    ? 'Approved'
    : form.customer_decision === 'declined'
      ? 'Declined'
      : form.customer_decision === 'pending'
        ? 'Waiting approval'
        : form.estimate_sent_at || form.quote_last_sent_at
          ? 'Sent'
          : 'Not sent'
  const existingServiceSummary = initialData ? jobServiceSummary(initialData) : ''
  const selectedCustomer = customers.find(c => c.id === form.customer_id)
  const selectedServiceRowsForSummary = suspensionServiceRows.filter(s => selectedServices.includes(s.id))
  const technicianNames = teamMembers.map(member => member.name)
  const currentTechnician = String(form.technician || '').trim()
  const currentTechnicianMissing = currentTechnician && !technicianNames.includes(currentTechnician)
  const selectedTechnicianId = String(form.technician_id || '').trim()
  const selectedTechnician = selectedTechnicianId
    ? teamMembers.find(member => String(member.id || '') === selectedTechnicianId)
    : null
  const scheduleSnapshotRows = selectedServiceRowsForSummary.length
    ? selectedServiceRowsForSummary.map(suspensionServiceSnapshot)
    : jobMeta.service_snapshot
  const plannedScheduleMinutes = plannedMinutesForJob({
    ...jobMeta,
    ...form,
    service_snapshot: scheduleSnapshotRows,
  })
  const canOverrideSchedule = canOverrideScheduleCapacity(profile)
  const selectedServiceSummary = selectedServiceRowsForSummary.map(s => s.name).join(', ') || existingServiceSummary || 'No service selected'
  const approvedExtraServiceRows = extraServiceLineJobScopeRows(extraServiceLineRows)
  const selectedServiceScopeSummary = jobServiceScopeSummary({
    module_key: 'suspension',
    service_summary: selectedServiceSummary,
    job_extra_service_line_requests: extraServiceLineRows,
  }) || selectedServiceSummary
  const targetServiceLines = selectedServiceRowsForSummary.map(service => {
    const lineTotal = Number(service.base_price || 0)
    return {
      job_service_id: '',
      service_id: service.id,
      name: service.name,
      category: service.category || '',
      is_addon: !!service.is_addon,
      quantity: 1,
      base_price: lineTotal,
      line_total: lineTotal,
    }
  })
  const selectedTargetLines = targetServiceLines.filter(line => adjustmentForm.target_line_ids.includes(line.service_id))
  const adjustmentPreview = priceAdjustmentAmount({
    ...adjustmentForm,
    value: adjustmentForm.value,
    target_lines: adjustmentForm.target_scope === 'job' ? [] : selectedTargetLines,
  }, financials.subtotal)
  const selectedComponentSummary = selectedComponent
    ? `${selectedComponent.year ? selectedComponent.year + ' ' : ''}${selectedComponent.model}`
    : 'No component linked'
  const selectedCustomerSummary = selectedCustomer
    ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
    : 'No customer selected'
  const currentStatus = normaliseJobStatus(form.status)
  const isOwner = profile?.role === 'owner' || profile?.is_superadmin
  const adjustmentApprovalRequired = effectiveLoyaltyWorkshop?.discount_approval_required === true
  const adjustmentLocked = jobPaymentMeta({ ...jobMeta, ...form }).key === 'paid'
  const loyaltyProgress = loyaltyProgressForCustomer({
    workshop: effectiveLoyaltyWorkshop,
    jobs: loyaltyJobs,
    components,
    rewards: loyaltyRewards,
    customerId: form.customer_id,
  })
  const canApplyLoyaltyByRole = isOwner || !adjustmentApprovalRequired
  const loyaltyAlreadyAppliedToJob = loyaltyRewards.some(reward => reward.status === 'applied' && reward.applied_job_id === initialData?.id)
  const loyaltyEligibleJob = jobCanReceiveLoyaltyReward({ ...jobMeta, ...form }, selectedComponent, effectiveLoyaltyWorkshop)
  const timelineJob = {
    ...(initialData || {}),
    ...jobMeta,
    ...form,
    job_price_adjustments: priceAdjustments,
    job_extra_service_line_requests: extraServiceLineRows,
    status: currentStatus,
    service_summary: selectedServiceSummary,
    total_charged: displayedTotal,
  }
  const nextAction = jobNextAction(timelineJob)
  const paymentMeta = jobPaymentMeta(timelineJob)
  const progressItems = jobProgressMeta(timelineJob)
  const statusMeta = jobStatusMeta({ ...form, status: currentStatus })
  const canInspect = isEdit && currentStatus !== 'cancelled'
  const canSendQuote = isEdit && currentStatus !== 'cancelled' && (!inheritedServicePrices || !['approved','declined'].includes(form.customer_decision))
  const canRecordDecision = isEdit && (currentStatus === 'estimate_sent' || form.customer_decision === 'pending')
  const canSignOffQc = isEdit && currentStatus !== 'cancelled' && currentStatus !== 'completed'
  const protectedDeleteReason = isEdit && hasProtectedDeleteMarkers(initialData) ? PROTECTED_DELETE_MESSAGE : ''
  const componentType = String(selectedComponent?.type || '').toLowerCase()
  const templateOptions = serviceTemplates.filter(template => {
    if (isAddonTemplate(template)) return false
    if (!componentType) return true
    return template.component_type === componentType || template.component_type === 'setup' || template.component_type === 'any'
  })
  const activeTemplateSnapshot = normaliseTemplateSnapshot(form.service_template_snapshot)
  const liveTemplateFor = templateRef => {
    if (!templateRef) return null
    const refId = typeof templateRef === 'string' ? templateRef : templateRef.id
    const refKey = typeof templateRef === 'object' ? templateRef.template_key : ''
    const refWorkshopId = typeof templateRef === 'object' ? templateRef.workshop_id : ''
    return serviceTemplates.find(template => template.id === refId) ||
      serviceTemplates.find(template => refKey && refWorkshopId && template.template_key === refKey && template.workshop_id === refWorkshopId) ||
      serviceTemplates.find(template => refKey && template.template_key === refKey && template.workshop_id === workshopId) ||
      serviceTemplates.find(template => refKey && template.template_key === refKey) ||
      null
  }
  const activeTemplate = mergeTemplateDefinitions(
    liveTemplateFor(activeTemplateSnapshot) || liveTemplateFor(form.service_template_id),
    activeTemplateSnapshot
  )
  const activeTemplateId = activeTemplate?.id || form.service_template_id || ''
  const templateSelectOptions = activeTemplate && activeTemplateId && !templateOptions.some(template => template.id === activeTemplateId)
    ? [activeTemplate, ...templateOptions]
    : templateOptions
  const templateSelectValue = templateChoice || activeTemplateId || ''
  const activeAddonSnapshots = normaliseTemplateArray(form.service_template_addon_snapshots)
  const activeAddonIds = normaliseTemplateArray(form.service_template_addon_ids)
  const activeAddons = activeAddonSnapshots.length
    ? activeAddonSnapshots.map(snapshot => mergeTemplateDefinitions(liveTemplateFor(snapshot), snapshot)).filter(Boolean)
    : serviceTemplates.filter(template => activeAddonIds.includes(template.id))
  const availableAddonKeys = arrayFromTemplate(activeTemplate, 'available_addons')
  const addonOptions = activeTemplate
    ? serviceTemplates.filter(template =>
      isAddonTemplate(template) &&
      template.active !== false &&
      (!availableAddonKeys.length || availableAddonKeys.includes(template.template_key))
    )
    : []
  const activeRuleTemplates = [activeTemplate, ...activeAddons].filter(Boolean)
  const hasTemplateRules = activeRuleTemplates.length > 0
  const activeMeasurementSections = activeRuleTemplates.flatMap(template => normaliseMeasurementSections(template.measurement_schema))
  const measurementSectionKeys = new Set()
  const mergedMeasurementSections = activeMeasurementSections.reduce((sections, section) => {
    const sectionKey = section.key || section.title
    const existing = sections.find(item => item.key === sectionKey)
    if (existing) {
      const fieldKeys = new Set(existing.fields.map(field => field.key || field.field || field.label))
      section.fields.forEach(field => {
        const fieldKey = field.key || field.field || field.label
        if (!fieldKeys.has(fieldKey)) existing.fields.push(field)
      })
      return sections
    }
    if (!measurementSectionKeys.has(sectionKey)) {
      measurementSectionKeys.add(sectionKey)
      sections.push({ ...section, fields: [...section.fields] })
    }
    return sections
  }, [])
  const measurementSettingSections = mergedMeasurementSections.filter(section => section.category === 'settings')
  const measurementServiceLogSections = mergedMeasurementSections.filter(section => section.category !== 'settings')
  const visibleScopeFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'scope_options', 'scope_fields')))
  const visibleSettingsFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'visible_settings_fields')))
  const visibleServiceLogFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'visible_service_log_fields')))
  const visibleInspectionFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'visible_inspection_checks', 'inspection_fields')))
  const visibleQcFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'visible_qc_checks', 'qc_fields')))
  const visibleHandoverFields = uniqueValues(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'visible_handover_fields')))
  const hiddenTemplateFields = new Set(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'hidden_fields')))
  const optionalTemplateFields = new Set(activeRuleTemplates.flatMap(template => arrayFromTemplate(template, 'optional_collapsed_fields')))
  const activeTemplateType = String(activeTemplate?.component_type || selectedComponent?.type || '').toLowerCase()
  const hasAirSpringWork = activeRuleTemplates.some(template =>
    arrayFromTemplate(template, 'scope_fields', 'scope_options').includes('sc_air_spring')
  )

  function templateFieldVisible(group, key, visibleList) {
    if (!hasTemplateRules) return true
    if (group === 'settings' && key === 'preload' && activeTemplateType === 'fork') return false
    if (group === 'service_log' && key === 'aircan_grease' && activeTemplateType === 'fork' && !hasAirSpringWork) return false
    if (hiddenTemplateFields.has(key) || hiddenTemplateFields.has(`${group}.${key}`)) return false
    if (!visibleList.length) return true
    return visibleList.includes(key) || visibleList.includes(`${group}.${key}`)
  }

  function templateFieldOptional(group, key) {
    return optionalTemplateFields.has(key) || optionalTemplateFields.has(`${group}.${key}`)
  }

  function selectedAddonMatches(template) {
    return activeAddons.some(addon => addon.id === template.id || addon.template_key === template.template_key)
  }

  function templateFieldLabels(fields = []) {
    return (fields || [])
      .map(field => TEMPLATE_FIELD_LABELS[field] || field)
      .filter(Boolean)
  }

  function templateSnapshot(template) {
    return {
      id: template.id,
      workshop_id: template.workshop_id || null,
      template_key: template.template_key,
      name: template.name,
      component_type: template.component_type,
      brand: template.brand,
      service_category: template.service_category,
      service_level: template.service_level,
      description: template.description,
      is_addon: !!template.is_addon,
      scope_fields: template.scope_fields || [],
      scope_options: template.scope_options || template.scope_fields || [],
      inspection_fields: template.inspection_fields || [],
      qc_fields: template.qc_fields || [],
      visible_settings_fields: template.visible_settings_fields || [],
      visible_service_log_fields: template.visible_service_log_fields || [],
      visible_inspection_checks: template.visible_inspection_checks || template.inspection_fields || [],
      visible_qc_checks: template.visible_qc_checks || template.qc_fields || [],
      visible_handover_fields: template.visible_handover_fields || [],
      measurement_schema: normaliseMeasurementSections(template.measurement_schema),
      default_reminder: template.default_reminder || {},
      hidden_fields: template.hidden_fields || [],
      optional_collapsed_fields: template.optional_collapsed_fields || [],
      available_addons: template.available_addons || [],
      default_addons: template.default_addons || [],
      recommended_defaults: template.recommended_defaults || {},
      applied_at: new Date().toISOString(),
    }
  }

  function applyServiceTemplate(template) {
    if (!template) return
    const snapshot = templateSnapshot(template)
    const defaults = snapshot.recommended_defaults || {}
    setForm(f => {
      const updates = {
        service_template_id: template.id,
        service_template_snapshot: snapshot,
        service_template_addon_ids: [],
        service_template_addon_snapshots: [],
      }

      SCOPE_FIELDS.forEach(field => { updates[field] = false })
      ;(snapshot.scope_fields || []).forEach(field => {
        if (BOOL_FIELDS.includes(field)) updates[field] = true
      })

      Object.entries(defaults).forEach(([key, value]) => {
        if (f[key] === '' || f[key] === null || f[key] === undefined) updates[key] = value
      })

      return { ...f, ...updates }
    })
    setTemplateChoice(template.id)
    setTemplateDetailsOpen(false)
    setTemplateNotice(`${template.name} applied. Scope has been pre-selected; inspection and QC remain for the technician to complete.`)
  }

  function clearServiceTemplate() {
    setForm(f => ({ ...f, service_template_id: '', service_template_snapshot: null, service_template_addon_ids: [], service_template_addon_snapshots: [] }))
    setTemplateChoice('')
    setTemplateDetailsOpen(true)
    setTemplateNotice('Template cleared from this job. Existing checklist ticks were left unchanged.')
  }

  function handleTemplateSelectChange(value) {
    setTemplateChoice(value && value !== activeTemplateId ? value : '')
    if (value) setTemplateDetailsOpen(true)
  }

  function toggleTemplateAddon(template, checked) {
    const existing = activeAddons.filter(addon => addon.id !== template.id && addon.template_key !== template.template_key)
    const next = checked ? [...existing, templateSnapshot(template)] : existing
    setForm(f => {
      const updates = {
        service_template_addon_ids: next.map(addon => addon.id).filter(Boolean),
        service_template_addon_snapshots: next,
      }

      next.forEach(addon => {
        ;(addon.scope_fields || []).forEach(field => {
          if (BOOL_FIELDS.includes(field)) updates[field] = true
        })
        Object.entries(addon.recommended_defaults || {}).forEach(([key, value]) => {
          if (f[key] === '' || f[key] === null || f[key] === undefined) updates[key] = value
        })
      })

      return { ...f, ...updates }
    })
  }

  useEffect(() => {
    if (!quoteEmail && selectedCustomer?.email) setQuoteEmail(selectedCustomer.email)
  }, [quoteEmail, selectedCustomer?.email])

  function numericOrNull(value) {
    if (value === '' || value === null || value === undefined) return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  function firstNumberText(value) {
    const match = String(value || '').match(/-?\d+(\.\d+)?/)
    return match ? match[0] : ''
  }

  function isSpringRateValue(value) {
    const raw = String(value || '').toLowerCase()
    const springType = String(selectedComponent?.spring_type || selectedComponent?.notes || '').toLowerCase()
    return Boolean(raw && (raw.includes('lb') || raw.includes('n/mm') || raw.includes('spring') || raw.includes('coil') || springType.includes('coil')))
      && !raw.includes('psi')
  }

  function settingValue(job, key) {
    const after = String(job[`a_${key}`] || '').trim()
    if (after) return after
    return String(job[`b_${key}`] || '').trim()
  }

  function structuredSettingValue(job, key) {
    const measurements = normaliseObject(job.structured_measurements)
    const after = String(measurements[`${key}_after`] || '').trim()
    if (after) return after
    const before = String(measurements[`${key}_before`] || '').trim()
    if (before) return before
    return String(measurements[key] || '').trim()
  }

  function firstSettingValue(job, keys = [], legacyKey = '') {
    for (const key of keys) {
      const value = structuredSettingValue(job, key)
      if (value) return value
    }
    return legacyKey ? settingValue(job, legacyKey) : ''
  }

  function jobMeasurementValue(job, field, side = null) {
    const measurements = normaliseObject(job.structured_measurements)
    if (side === 'before' && field.before) return String(job[field.before] || '').trim()
    if (side === 'after' && field.after) return String(job[field.after] || '').trim()
    if (side && field.kind === 'before_after') return String(measurements[`${field.key}_${side}`] || '').trim()
    if (field.field) return String(job[field.field] || '').trim()
    return String(measurements[field.key] || '').trim()
  }

  function setupSettingsSnapshotFromJob(job) {
    const seen = new Set()
    return activeRuleTemplates
      .flatMap(template => normaliseMeasurementSections(template.measurement_schema))
      .filter(section => section.category === 'settings')
      .flatMap(section => section.fields.map(field => ({ section, field })))
      .filter(({ field }) => field.visibility !== 'internal' && field.customer_visible !== false)
      .map(({ section, field }) => {
        const key = field.key || field.field || field.label
        if (!key || seen.has(key)) return null
        seen.add(key)
        const after = field.kind === 'before_after'
          ? jobMeasurementValue(job, field, 'after') || jobMeasurementValue(job, field, 'before')
          : jobMeasurementValue(job, field)
        if (!after) return null
        return {
          key,
          label: field.label || key,
          value: after,
          unit: field.unit || '',
          section: section.title || 'Setup',
        }
      })
      .filter(Boolean)
  }

  function applySag(setupForm, prefix, rawValue) {
    const raw = String(rawValue || '').trim()
    if (!raw) return
    const field = raw.toLowerCase().includes('mm') ? `${prefix}_sag_mm` : `${prefix}_sag_pct`
    setupForm[field] = firstNumberText(raw)
  }

  function setupFormFromJobAfterSettings(job) {
    const componentType = String(selectedComponent?.type || '').toLowerCase()
    if (!selectedComponent?.id || !selectedComponent?.bike_id || !['fork', 'shock'].includes(componentType)) return null

    const setupForm = {
      name: 'Current setup verified by service log',
      setup_type: 'workshop_current',
      is_active: true,
      terrain_label: '',
      notes: job.setup_rec || '',
    }

    if (componentType === 'fork') {
      const airValue = firstSettingValue(job, ['air_pressure', 'air_pressure_main'], 'air')
      const springValue = firstSettingValue(job, ['coil_spring_rate'])
      if (springValue || isSpringRateValue(airValue)) setupForm.front_spring_rate = springValue || airValue
      else setupForm.front_air_pressure = firstNumberText(airValue)
      setupForm.front_sag_mm = firstNumberText(firstSettingValue(job, ['sag_mm']))
      setupForm.front_sag_pct = firstNumberText(firstSettingValue(job, ['sag_pct']))
      if (!setupForm.front_sag_mm && !setupForm.front_sag_pct) applySag(setupForm, 'front', settingValue(job, 'sag'))
      setupForm.fork_tokens = firstNumberText(firstSettingValue(job, ['token_count', 'volume_spacers', 'air_volume_spacers'], 'tokens'))
      setupForm.fork_lsc = firstNumberText(firstSettingValue(job, ['lsc', 'compression'], 'lsc'))
      setupForm.fork_hsc = firstNumberText(firstSettingValue(job, ['hsc'], 'hsc'))
      setupForm.fork_lsr = firstNumberText(firstSettingValue(job, ['lsr', 'rebound'], 'lsr'))
      setupForm.fork_hsr = firstNumberText(firstSettingValue(job, ['hsr'], 'hsr'))
      setupForm.fork_mode = firstSettingValue(job, ['lockout_mode', 'threshold_mode', 'compression_mode', 'pedal_mode', 'lok_mode'], 'pedal')
      setupForm.fork_hbo = firstSettingValue(job, ['hydraulic_bottom_out', 'hbc'], 'hbo')
    } else {
      const airValue = firstSettingValue(job, ['air_pressure', 'air_pressure_main'], 'air')
      const springValue = firstSettingValue(job, ['coil_spring_rate'])
      if (springValue || isSpringRateValue(airValue)) setupForm.rear_spring_rate = springValue || airValue
      else setupForm.rear_air_pressure = firstNumberText(airValue)
      setupForm.rear_sag_mm = firstNumberText(firstSettingValue(job, ['sag_mm']))
      setupForm.rear_sag_pct = firstNumberText(firstSettingValue(job, ['sag_pct']))
      if (!setupForm.rear_sag_mm && !setupForm.rear_sag_pct) applySag(setupForm, 'rear', settingValue(job, 'sag'))
      setupForm.shock_preload_turns = firstNumberText(firstSettingValue(job, ['preload_turns'], 'preload'))
      setupForm.shock_spacers = firstNumberText(firstSettingValue(job, ['volume_spacers', 'air_volume_spacers'], 'tokens'))
      setupForm.shock_lsc = firstNumberText(firstSettingValue(job, ['lsc'], 'lsc'))
      setupForm.shock_hsc = firstNumberText(firstSettingValue(job, ['hsc'], 'hsc'))
      setupForm.shock_rebound = firstNumberText(firstSettingValue(job, ['lsr', 'rebound'], 'lsr'))
      setupForm.shock_mode = firstSettingValue(job, ['threshold_mode', 'lockout_mode', 'compression_mode', 'pedal_mode', 'lok_mode'], 'pedal')
      setupForm.shock_hbo = firstSettingValue(job, ['hydraulic_bottom_out', 'hbc'], 'hbo')
    }

    const meaningfulFields = Object.entries(setupForm).filter(([key, value]) =>
      !['name', 'setup_type', 'is_active', 'terrain_label', 'notes'].includes(key)
        && value !== ''
        && value !== null
        && value !== undefined
    )
    return meaningfulFields.length ? setupForm : null
  }

  async function syncWorkshopSetupFromJob(savedJob) {
    const setupForm = setupFormFromJobAfterSettings(savedJob)
    if (!setupForm) return

    const payload = {
      ...setupPayloadFromForm(setupForm),
      workshop_id: workshopId,
      bike_id: selectedComponent.bike_id,
      component_id: selectedComponent.id,
      source_job_id: savedJob.id,
      rider_auth_user_id: null,
      created_by_role: profile?.role || 'workshop',
      created_by: profile?.id || null,
      setup_settings: setupSettingsSnapshotFromJob(savedJob),
    }

    try {
      await supabase
        .from('setup_snapshots')
        .update({ is_active: false })
        .eq('workshop_id', workshopId)
        .eq('component_id', selectedComponent.id)
        .eq('setup_type', 'workshop_current')

      const { data: existing, error: existingError } = await supabase
        .from('setup_snapshots')
        .select('id')
        .eq('workshop_id', workshopId)
        .eq('component_id', selectedComponent.id)
        .eq('setup_type', 'workshop_current')
        .limit(1)
        .maybeSingle()

      if (existingError) throw existingError

      if (existing?.id) {
        const { error: updateError } = await supabase
          .from('setup_snapshots')
          .update(payload)
          .eq('id', existing.id)
          .eq('workshop_id', workshopId)
        if (updateError) throw updateError
      } else {
        const { error: insertError } = await supabase
          .from('setup_snapshots')
          .insert(payload)
        if (insertError) throw insertError
      }
    } catch (err) {
      console.warn('Workshop setup sync skipped:', err.message || err)
    }
  }

  function totalFor(values, rows = priceAdjustments) {
    return jobFinancials({
      ...jobMeta,
      ...values,
      job_price_adjustments: rows,
      job_extra_service_line_requests: extraServiceLineRows,
    }).totalCost
  }

  function handleExtraServiceLineRowsChange(rows = []) {
    setExtraServiceLineRows(Array.isArray(rows) ? rows : [])
  }

  async function handleExtraServiceLineChanged() {
    try {
      await reloadJobPricing()
    } catch (err) {
      setWorkflowError(err.message || 'Could not refresh extra service-line totals.')
    }
  }

  function setServiceMenuTotal(total) {
    const serviceCost = Number(total || 0).toFixed(2)
    setForm(f => ({
      ...f,
      labour_charge: serviceCost,
      total_charged: totalFor({ ...f, labour_charge: serviceCost }).toFixed(2),
    }))
  }

  function buildJobPayload(overrides = {}) {
    const payload = { ...form, ...overrides, workshop_id: workshopId }
    payload.status = normaliseJobStatus(payload.status)
    payload.physical_location = String(payload.physical_location || '').trim()
    payload.scheduled_date = payload.scheduled_date || null
    payload.due_date = payload.due_date || null
    const assignedTechnician = payload.technician_id
      ? teamMembers.find(member => String(member.id || '') === String(payload.technician_id || ''))
      : null
    payload.technician = assignedTechnician ? assignedTechnician.name : String(payload.technician || '').trim()
    payload.technician_id = payload.technician_id || null
    payload.scheduled_duration_mins = payload.scheduled_duration_mins === '' || payload.scheduled_duration_mins === null
      ? null
      : Math.max(0, Math.round(Number(payload.scheduled_duration_mins) || 0))
    payload.bike_id = selectedComponent?.bike_id || payload.bike_id || null
    const selectedServiceRows = suspensionServiceRows.filter(s => selectedServices.includes(s.id))
    payload.service_ids = selectedServices
    payload.service_snapshot = inheritedServicePrices ? initialData.service_snapshot : selectedServiceRows.length
      ? selectedServiceRows.map(suspensionServiceSnapshot)
      : normaliseTemplateArray(initialData?.service_snapshot)
    const selectedServiceSummary = selectedServiceRows.map(s => {
      const price = Number(s.base_price || 0)
      return `${s.name}${price > 0 ? ` - GBP ${price.toFixed(2)}` : ''}`
    }).join('\n')
    payload.service_summary = inheritedServicePrices ? initialData.service_summary : selectedServiceSummary || initialData?.service_summary || ''
    payload.parts_used = normaliseJobPartsUsed(payload.parts_used)
    if (payload.parts_used.length) {
      payload.parts_cost = jobPartsTotals(payload.parts_used).costTotal
    }
    ;['parts_cost','estimate_parts_cost','labour_charge','total_charged','duration_mins'].forEach(k => {
      if (payload[k] === '' || payload[k] === null) payload[k] = 0
    })
    if (payload.estimate_amount === '') payload.estimate_amount = null
    payload.total_charged = totalFor(payload)
    ;['inspection_completed_at','estimate_sent_at','customer_decision_at','quote_last_sent_at','report_sent_at','qc_signed_off_at'].forEach(k => {
      if (payload[k] === '') payload[k] = null
    })
    if (!['passed','failed'].includes(payload.qc_status)) payload.qc_status = ''
    if (!payload.qc_signed_off_by) payload.qc_signed_off_by = null
    if (!payload.component_id) payload.component_id = null
    if (!payload.next_service_date) payload.next_service_date = null
    payload.structured_measurements = normaliseObject(payload.structured_measurements)
    if (!Object.keys(payload.structured_measurements).length && !initialData?.structured_measurements) {
      delete payload.structured_measurements
    }
    if (activeTemplate) {
      payload.service_template_snapshot = templateSnapshot(activeTemplate)
    }
    const hasTemplatePayload = Boolean(
      payload.service_template_id ||
      payload.service_template_snapshot ||
      normaliseTemplateArray(payload.service_template_addon_ids).length ||
      normaliseTemplateArray(payload.service_template_addon_snapshots).length ||
      initialData?.service_template_id ||
      initialData?.service_template_snapshot ||
      initialData?.service_template_addon_ids ||
      initialData?.service_template_addon_snapshots
    )
    if (hasTemplatePayload) {
      if (!payload.service_template_id) payload.service_template_id = null
      if (!payload.service_template_snapshot) payload.service_template_snapshot = null
      payload.service_template_addon_ids = normaliseTemplateArray(payload.service_template_addon_ids)
      payload.service_template_addon_snapshots = normaliseTemplateArray(payload.service_template_addon_snapshots)
    } else {
      delete payload.service_template_id
      delete payload.service_template_snapshot
      delete payload.service_template_addon_ids
      delete payload.service_template_addon_snapshots
    }
    if (!payload.sc_dropper_service && initialData?.sc_dropper_service === undefined) delete payload.sc_dropper_service
    if (inheritedServicePrices) {
      const numericFields = ['labour_charge', 'total_charged', 'parts_cost', 'estimate_amount', 'estimate_parts_cost']
      const unchanged = numericFields.every(key => Number(payload[key] || 0) === Number(initialData[key] || 0))
        && JSON.stringify([...(payload.service_ids || [])].sort()) === JSON.stringify([...(initialData.service_ids || [])].sort())
        && JSON.stringify(normaliseJobPartsUsed(payload.parts_used)) === JSON.stringify(normaliseJobPartsUsed(initialData.parts_used))
      if (!unchanged) throw new Error('Recorded service prices need an explicit reviewed quote before they can change.')
      // Keep database nulls and original snapshots intact when saving notes/QC.
      for (const key of [...numericFields, 'parts_used', 'pricing_mode', 'price_status']) {
        if (Object.prototype.hasOwnProperty.call(initialData, key)) payload[key] = initialData[key]
        else delete payload[key]
      }
    }
    return payload
  }

  async function saveCurrentJobForm(overrides = {}) {
    const payload = buildJobPayload(overrides)
    const { workshop_id, scheduled_date, technician, technician_id, scheduled_duration_mins, ...updatePayload } = payload
    return supabase
      .from('jobs')
      .update(updatePayload)
      .eq('id', initialData.id)
      .select(JOB_FINANCIAL_SELECT)
      .single()
  }

  function openWorkflow(mode) {
    setWorkflowMode(mode)
    setWorkflowNotice('')
    setWorkflowError('')
    if (mode === 'decision' && !form.customer_decision) {
      setForm(f => ({ ...f, customer_decision: 'approved' }))
    }
    if (!quoteEmail && selectedCustomer?.email) setQuoteEmail(selectedCustomer.email)
  }

  async function logWorkflow(action, metadata = {}) {
    if (!initialData?.id) return
    try {
      await supabase.from('audit_events').insert({
        workshop_id: workshopId,
        entity_type: 'job',
        entity_id: initialData.id,
        action,
        metadata,
      })
    } catch {}
  }

  async function updateJobWorkflow(payload, notice, auditAction) {
    if (!initialData?.id) return
    setWorkflowSaving(true)
    setWorkflowError('')
    setWorkflowNotice('')

    const { data, error } = await saveCurrentJobForm(payload)

    setWorkflowSaving(false)
    if (error) {
      setWorkflowError(error.message)
      return
    }

    setForm(f => ({ ...f, ...buildInitialForm(data, preCustomerId, preComponentId) }))
    setJobMeta(data)
    setPriceAdjustments(normalisePriceAdjustments(data))
    setExtraServiceLineRows(data?.job_extra_service_line_requests || [])
    await syncWorkshopSetupFromJob(data)
    setWorkflowMode(null)
    setWorkflowNotice(notice)
    await logWorkflow(auditAction, { status: data.status })
    return data
  }

  async function openCustomerFromJob() {
    if (!isEdit || !initialData?.id || !form.customer_id || customerNavSaving) return
    setCustomerNavSaving(true)
    setError(null)

    try {
      const { data, error: saveError } = await saveCurrentJobForm()

      if (saveError) {
        setCustomerNavSaving(false)
        setError(saveError.message || 'Could not save the current job before opening the customer.')
        return
      }

      if (data) {
        setJobMeta(data)
        setPriceAdjustments(normalisePriceAdjustments(data))
        setExtraServiceLineRows(data?.job_extra_service_line_requests || [])
        setForm(f => ({ ...f, ...buildInitialForm(data, preCustomerId, preComponentId) }))
        await syncWorkshopSetupFromJob(data)
      }

      if (onSaved) onSaved(data)
      navigate(`/customers?customer=${encodeURIComponent(form.customer_id)}`)
    } catch (err) {
      setCustomerNavSaving(false)
      setError(err.message || 'Could not save the current job before opening the customer.')
    }
  }

  async function saveInspectionWorkflow() {
    const now = new Date().toISOString()
    const payload = {
      inspection_completed_at: now,
      inspection_summary: String(form.inspection_summary || '').trim(),
      condition_notes: String(form.condition_notes || '').trim(),
      ins_stanchion: !!form.ins_stanchion,
      ins_csu: !!form.ins_csu,
      ins_bushing_play: !!form.ins_bushing_play,
      ins_air_shaft: !!form.ins_air_shaft,
      ins_damp_shaft: !!form.ins_damp_shaft,
      ins_eyelet: !!form.ins_eyelet,
      ins_bearing: !!form.ins_bearing,
      ins_wiper: !!form.ins_wiper,
      ins_oil_contam: !!form.ins_oil_contam,
      ins_scoring: !!form.ins_scoring,
      ins_adjusters: !!form.ins_adjusters,
      ins_lockout: !!form.ins_lockout,
    }
    await updateJobWorkflow(payload, 'Inspection saved.', 'job.inspection_completed')
  }

  function currentSignerName() {
    return String(profile?.full_name || form.technician || user?.email || 'Workshop').trim()
  }

  async function saveQcWorkflow(status) {
    const passed = status === 'passed'
    const now = new Date().toISOString()
    const visibleQcIds = QC_FIELDS
      .filter(([, id]) => qcFieldIncluded(id))
      .map(([, id]) => id)
    const payload = {
      qc_status: passed ? 'passed' : 'failed',
      qc_signed_off_at: now,
      qc_signed_off_by: profile?.id || null,
      qc_signed_off_by_name: currentSignerName(),
      qc_notes: String(form.qc_notes || '').trim(),
    }

    visibleQcIds.forEach(id => {
      payload[id] = passed ? true : !!form[id]
    })

    await updateJobWorkflow(
      payload,
      passed ? 'QC passed and signed off. Report is ready to send.' : 'QC failed and recorded.',
      passed ? 'job.qc_passed' : 'job.qc_failed'
    )
  }

  async function clearQcSignOff() {
    const payload = {
      qc_status: '',
      qc_signed_off_at: null,
      qc_signed_off_by: null,
      qc_signed_off_by_name: '',
      qc_notes: String(form.qc_notes || '').trim(),
    }
    await updateJobWorkflow(payload, 'QC sign-off cleared.', 'job.qc_signoff_cleared')
  }

  async function markQuoteSentWithoutEmail() {
    const amount = numericOrNull(form.estimate_amount)
    const estimatePartsCost = numericOrNull(form.estimate_parts_cost)
    const payload = {
      status: 'estimate_sent',
      estimate_amount: amount,
      estimate_parts_cost: estimatePartsCost ?? 0,
      estimate_notes: String(form.estimate_notes || '').trim(),
      estimate_sent_at: new Date().toISOString(),
      customer_decision: 'pending',
      total_charged: totalFor({ ...form, estimate_amount: amount ?? '', estimate_parts_cost: estimatePartsCost ?? '', customer_decision: 'pending' }),
    }
    await updateJobWorkflow(payload, 'Quote marked as sent.', 'job.estimate_sent')
  }

  async function sendQuoteEmail() {
    const to = String(quoteEmail || '').trim()
    if (!initialData?.id || !to) {
      setWorkflowError('Add a customer email before sending the quote.')
      return
    }

    setWorkflowSaving(true)
    setWorkflowError('')
    setWorkflowNotice('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending the quote.')

      const amount = numericOrNull(form.estimate_amount)
      const estimatePartsCost = numericOrNull(form.estimate_parts_cost)
      if (inheritedServicePrices) {
        if (process.env.REACT_APP_VAT_BUILD3 !== 'true') throw new Error('Reviewed quote delivery is not enabled.')
        // Validate visible edits, but let the quote authority own its transition.
        // Form defaults must not rewrite recorded null amounts or decisions.
        buildJobPayload()
        if (Number(amount || 0) || Number(estimatePartsCost || 0)) throw new Error('Additional amounts need an attributable reviewed quote before sending.')
      } else {
        const { error: saveError } = await saveCurrentJobForm({
          estimate_amount: amount,
          estimate_parts_cost: estimatePartsCost ?? 0,
          estimate_notes: String(form.estimate_notes || '').trim(),
        })
        if (saveError) throw new Error(saveError.message || 'Could not save the current job before sending the quote.')
      }

      const res = await fetch('/.netlify/functions/send-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          jobId: initialData.id,
          to,
          estimateAmount: form.estimate_amount,
          estimatePartsCost: form.estimate_parts_cost,
          estimateNotes: form.estimate_notes,
          origin: window.location.origin,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Quote email failed.')

      if (data.job) setForm(f => ({ ...f, ...buildInitialForm(data.job, preCustomerId, preComponentId) }))
      setWorkflowMode(null)
      setWorkflowNotice('Quote emailed to the customer with an approval link.')
    } catch (err) {
      setWorkflowError(err.message || 'Quote email failed.')
    } finally {
      setWorkflowSaving(false)
    }
  }

  async function recordCustomerDecision() {
    const decision = form.customer_decision === 'declined' ? 'declined' : 'approved'
    const payload = {
      customer_decision: decision,
      customer_decision_at: new Date().toISOString(),
      customer_decision_note: String(form.customer_decision_note || '').trim(),
      total_charged: totalFor({ ...form, customer_decision: decision }),
    }
    if (decision === 'approved') payload.status = 'received'
    const updated = await updateJobWorkflow(payload, decision === 'approved' ? 'Customer approval recorded.' : 'Customer decline recorded.', 'job.customer_decision_recorded')
    if (!updated || decision !== 'declined') return

    setWorkflowSaving(true)
    try {
      const lifecycleResult = await runJobLifecycleAction(supabase, 'cancel', updated.id, {
        reason: lifecycleNoteForAction('cancel', updated, 'Customer declined the quote.'),
      })
      const { data: refreshed } = await supabase
        .from('jobs')
        .select(JOB_FINANCIAL_SELECT)
        .eq('id', updated.id)
        .single()
      const cancelledJob = refreshed || lifecycleResult?.job || updated
      setForm(f => ({ ...f, ...buildInitialForm(cancelledJob, preCustomerId, preComponentId) }))
      setJobMeta(cancelledJob)
      setPriceAdjustments(normalisePriceAdjustments(cancelledJob))
      setExtraServiceLineRows(cancelledJob?.job_extra_service_line_requests || [])
      await syncWorkshopSetupFromJob(cancelledJob)
      setWorkflowNotice('Customer decline recorded.')
    } catch (err) {
      setWorkflowError(err.message || 'Customer decision was saved, but cancellation was blocked.')
    } finally {
      setWorkflowSaving(false)
    }
  }

  function setAdjustmentField(key, value) {
    setAdjustmentForm(current => ({ ...current, [key]: value }))
  }

  function toggleAdjustmentLine(serviceId) {
    setAdjustmentForm(current => {
      const exists = current.target_line_ids.includes(serviceId)
      return {
        ...current,
        target_line_ids: exists
          ? current.target_line_ids.filter(id => id !== serviceId)
          : [...current.target_line_ids, serviceId],
      }
    })
  }

  async function reloadJobPricing() {
    const { data, error: loadError } = await supabase
      .from('jobs')
      .select(JOB_FINANCIAL_SELECT)
      .eq('id', initialData.id)
      .single()
    if (loadError) throw loadError
    setJobMeta(data)
    setPriceAdjustments(normalisePriceAdjustments(data))
    setExtraServiceLineRows(data?.job_extra_service_line_requests || [])
    setForm(f => ({ ...f, ...buildInitialForm(data, preCustomerId, preComponentId) }))
    return data
  }

  async function syncStoredTotal(job) {
    const total = jobFinancials(job).totalCost
    const amountPence = Math.round(Math.max(0, Number(total) || 0) * 100)
    const storedPaymentAmountPence = Number(job.payment_amount_pence || 0)
    const storedJobAmountPence = Math.round(Math.max(0, Number(job.total_charged) || 0) * 100)
    const paymentAlreadyPaid = job.payment_status === 'paid' || Boolean(job.payment_paid_at)
    const hasExistingPaymentLink = Boolean(
      job.payment_link
      || job.stripe_checkout_session_id
      || job.stripe_payment_intent_id
      || job.stripe_payment_link_id
      || job.payment_requested_at
    )
    const existingPaymentAmountMatches = storedPaymentAmountPence > 0
      ? storedPaymentAmountPence === amountPence
      : storedJobAmountPence === amountPence
    const paymentRefreshFields = !paymentAlreadyPaid && hasExistingPaymentLink && amountPence > 0 && !existingPaymentAmountMatches
      ? {
          payment_link: null,
          stripe_payment_link_id: null,
          stripe_checkout_session_id: null,
          stripe_payment_intent_id: null,
          payment_provider: null,
          payment_amount_pence: null,
          payment_status: 'not_requested',
          payment_requested_at: null,
          payment_reminder_sent_at: null,
          payment_refresh_required: true,
          payment_refresh_reason: 'Suspension job total changed after stock usage was updated. Generate a fresh payment link before requesting payment.',
        }
      : {}
    const { data, error: updateError } = await supabase
      .from('jobs')
      .update({ total_charged: total, ...paymentRefreshFields })
      .eq('id', job.id)
      .select(JOB_FINANCIAL_SELECT)
      .single()
    if (updateError) throw updateError
    setJobMeta(data)
    setPriceAdjustments(normalisePriceAdjustments(data))
    setExtraServiceLineRows(data?.job_extra_service_line_requests || [])
    setForm(f => ({ ...f, ...buildInitialForm(data, preCustomerId, preComponentId) }))
    return data
  }

  async function handleStockUsageChanged() {
    try {
      const refreshed = await reloadJobPricing()
      const synced = await syncStoredTotal({
        ...refreshed,
        job_price_adjustments: normalisePriceAdjustments(refreshed),
      })
      const paymentWarning = synced.payment_refresh_required ? ' Existing payment link was cleared; generate a new one before requesting payment.' : ''
      setAdjustmentNotice(`Stock usage totals refreshed.${paymentWarning}`)
      if (onChanged) onChanged(synced)
    } catch (err) {
      setWorkflowError(err.message || 'Could not refresh stock usage totals.')
    }
  }

  async function createPriceAdjustment() {
    if (!initialData?.id) {
      setAdjustmentError('Save the job before adding discounts or adjustments.')
      return
    }
    if (adjustmentLocked) {
      setAdjustmentError('Paid jobs are locked. Discounts and adjustments cannot be changed.')
      return
    }
    const value = Number(adjustmentForm.value)
    if (!Number.isFinite(value) || value <= 0) {
      setAdjustmentError('Enter a value greater than zero.')
      return
    }
    if (adjustmentForm.value_type === 'percent' && value > 100) {
      setAdjustmentError('Percentage values must be 100 or less.')
      return
    }
    const targetScope = adjustmentForm.target_scope === 'job' ? 'job' : 'selected_service_lines'
    const targetLines = targetScope === 'job' ? [] : selectedTargetLines
    if (targetScope !== 'job' && !targetLines.length) {
      setAdjustmentError('Select at least one service or add-on line.')
      return
    }

    setAdjustmentSaving(true)
    setAdjustmentError('')
    setAdjustmentNotice('')
    try {
      const status = adjustmentApprovalRequired && !isOwner ? 'pending' : 'approved'
      const label = String(adjustmentForm.label || '').trim()
        || (adjustmentForm.kind === 'discount' ? 'Discount' : 'Adjustment')
      const row = {
        workshop_id: workshopId,
        job_id: initialData.id,
        kind: adjustmentForm.kind,
        value_type: adjustmentForm.value_type,
        value,
        calculated_amount: adjustmentPreview,
        label,
        reason: String(adjustmentForm.reason || '').trim(),
        status,
        target_scope: targetScope,
        target_lines: targetLines,
      }
      const { error: insertError } = await supabase.from('job_price_adjustments').insert(row)
      if (insertError) throw insertError
      const refreshed = await reloadJobPricing()
      const synced = status === 'approved'
        ? await syncStoredTotal({ ...refreshed, job_price_adjustments: normalisePriceAdjustments(refreshed) })
        : refreshed
      setAdjustmentForm(DEFAULT_ADJUSTMENT_FORM)
      const paymentWarning = synced.payment_refresh_required ? ' Existing payment link was cleared; generate a new one before requesting payment.' : ''
      setAdjustmentNotice(status === 'approved'
        ? `Adjustment applied.${paymentWarning}`
        : 'Adjustment proposed for owner approval.')
      if (onChanged) onChanged(synced)
    } catch (err) {
      setAdjustmentError(err.message || 'Could not save discount or adjustment.')
    } finally {
      setAdjustmentSaving(false)
    }
  }

  async function applyLoyaltyReward() {
    if (!initialData?.id) {
      setAdjustmentError('Save the job before applying a loyalty reward.')
      return
    }
    if (adjustmentLocked) {
      setAdjustmentError('Paid jobs are locked. Loyalty rewards cannot be applied.')
      return
    }
    if (loyaltyAlreadyAppliedToJob) {
      setAdjustmentError('A loyalty reward has already been applied to this job.')
      return
    }
    if (!loyaltyEligibleJob) {
      setAdjustmentError('This job is not eligible for the current loyalty categories.')
      return
    }
    if (loyaltyProgress.availableCount <= 0) {
      setAdjustmentError('This customer has no available loyalty reward yet.')
      return
    }
    if (!canApplyLoyaltyByRole) {
      setAdjustmentError('Owner approval is required to apply loyalty rewards.')
      return
    }

    setLoyaltyBusy(true)
    setAdjustmentError('')
    setAdjustmentNotice('')
    try {
      const { error: rpcError } = await supabase.rpc('apply_loyalty_reward', { p_job_id: initialData.id })
      if (rpcError) throw rpcError
      const refreshed = await reloadJobPricing()
      const synced = await syncStoredTotal({ ...refreshed, job_price_adjustments: normalisePriceAdjustments(refreshed) })
      await loadLoyalty()
      const paymentWarning = synced.payment_refresh_required ? ' Existing payment link was cleared; generate a new one before requesting payment.' : ''
      setAdjustmentNotice(`Loyalty reward applied.${paymentWarning}`)
      if (onChanged) onChanged(synced)
    } catch (err) {
      setAdjustmentError(err.message || 'Could not apply loyalty reward.')
    } finally {
      setLoyaltyBusy(false)
    }
  }

  async function save() {
    if (saving) return
    setSavedForm(null)
    try {
      await saveChanges()
    } catch (saveError) {
      setError(saveError.message || 'Could not save job.')
    } finally {
      setSaving(false)
    }
  }

  async function saveChanges() {
    if (vatIntake.blocked) { setError(vatIntake.error || 'Wait for reviewed prices.'); return }
    if (!form.customer_id) { setError('Select a customer.'); return }
    setSaving(true)
    setError(null)
    const payload = buildJobPayload()
    if (vatIntake.prices) {
      payload.labour_charge = vatIntake.gross
      payload.total_charged = vatIntake.gross
      payload.service_summary = vatIntake.prices.catalogue.map(line => line.name).join(', ')
    }
    const requestedStatus = normaliseJobStatus(form.status)
    const previousStatus = isEdit ? normaliseJobStatus(initialData?.status || 'received') : 'received'
    const lifecycleAction = lifecycleActionForStatusChange(previousStatus, requestedStatus)
    payload.status = statusForDirectJobSave(previousStatus, requestedStatus, 'received')
    let err
    let savedJob = null
    const scheduleChanged = isEdit && (
      String(initialData?.scheduled_date || '') !== String(payload.scheduled_date || '')
      || String(initialData?.technician_id || '') !== String(payload.technician_id || '')
      || String(initialData?.technician || '') !== String(payload.technician || '')
    )
    if (isEdit) {
      const { workshop_id, scheduled_date, technician, technician_id, scheduled_duration_mins, ...updatePayload } = payload
      const result = await supabase.from('jobs').update(updatePayload).eq('id', initialData.id).select(JOB_FINANCIAL_SELECT).single()
      err = result.error
      savedJob = result.data
    } else {
      // Generate invoice number atomically before insert
      const { data: invData } = vatIntake.prices ? { data: null } : await supabase.rpc('generate_invoice_number', { p_workshop_id: workshopId })
      if (invData) payload.invoice_number = invData
      const insertPayload = (({ scheduled_date, technician, technician_id, scheduled_duration_mins, ...rest }) => rest)(payload)
      try {
        const createResult = await runServiceJobCreateWithSchedule(supabase, insertPayload, {
          ...vatIntake.options,
          scheduledDate: payload.scheduled_date,
          technicianId: payload.technician_id,
          overrideOverCapacity: scheduleOverrideOverCapacity,
          note: 'Suspension job initial schedule set from the job card.',
        })
        savedJob = createResult?.job || null
      } catch (createErr) {
        err = createErr
      }
    }
    if (err) {
      setSaving(false)
      setError(err.message)
      return
    }
    if (scheduleChanged && savedJob?.id) {
      try {
        const scheduleResult = await runJobScheduleUpdate(supabase, savedJob.id, {
          scheduledDate: payload.scheduled_date,
          technicianId: payload.technician_id,
          overrideOverCapacity: scheduleOverrideOverCapacity,
          note: 'Suspension job schedule updated from the job card.',
        })
        savedJob = scheduleResult?.job || savedJob
      } catch (scheduleErr) {
        setSaving(false)
        setError(scheduleErr.message || 'Job saved, but the schedule change was blocked.')
        return
      }
    }
    if (err) setError(err.message)
    else {
      if (lifecycleAction && savedJob?.id) {
        try {
          const lifecycleResult = await runJobLifecycleAction(supabase, lifecycleAction, savedJob.id, {
            note: lifecycleNoteForAction(lifecycleAction, savedJob),
            reason: lifecycleNoteForAction(lifecycleAction, savedJob),
          })
          const refreshed = await supabase
            .from('jobs')
            .select(JOB_FINANCIAL_SELECT)
            .eq('id', savedJob.id)
            .single()
          savedJob = refreshed.data || lifecycleResult?.job || savedJob
        } catch (lifecycleErr) {
          setError(lifecycleErr.message || 'Job saved, but the lifecycle action was blocked.')
          return
        }
      }
      if (savedJob) {
        setJobMeta(savedJob)
        setPriceAdjustments(normalisePriceAdjustments(savedJob))
        setExtraServiceLineRows(savedJob?.job_extra_service_line_requests || [])
      }
      await syncWorkshopSetupFromJob(savedJob || payload)
      clearDraft()
      setSavedForm({ form, selectedServices })
      onSaved(savedJob)
    }
  }

  async function doDelete() {
    setDeleting(true)
    setError(null)
    if (hasProtectedDeleteMarkers(initialData)) {
      setDeleting(false)
      setConfirmDelete(false)
      setError(PROTECTED_DELETE_MESSAGE)
      return
    }
    try {
      if (await hasProtectedDeleteHistory(initialData.id)) {
        setDeleting(false)
        setConfirmDelete(false)
        setError(PROTECTED_DELETE_MESSAGE)
        return
      }
    } catch (err) {
      setDeleting(false)
      setError(err.message || 'Could not check job history before deleting.')
      return
    }

    const { error: err } = await supabase.from('jobs').delete().eq('id', initialData.id)
    setDeleting(false)
    if (err) setError(err.message)
    else onDeleted()
  }

  // Inline field helpers — not components, just functions returning JSX directly.
  // This avoids any child component re-mount on state change.
  const fi = (label, id, type='text', placeholder='') => (
    <div className="field">
      <label>{label}</label>
      <input type={type} value={form[id]||''} onChange={e => set(id, e.target.value)} placeholder={placeholder} />
    </div>
  )
  const setTechnicianAssignment = value => {
    const member = teamMembers.find(row => String(row.id || '') === String(value || ''))
    setForm(f => ({
      ...f,
      technician_id: value || '',
      technician: member?.name || '',
    }))
  }
  const technicianSelect = () => (
    <div className="field">
      <label>Technician</label>
      <select value={selectedTechnician?.id || ''} onChange={e => setTechnicianAssignment(e.target.value)}>
        <option value="">Unassigned</option>
        {teamMembers.map(member => (
          <option key={member.id} value={member.id}>
            {member.name}{member.role ? ` - ${member.role}` : ''}
          </option>
        ))}
      </select>
      {currentTechnicianMissing && <div className="field-hint">Legacy technician name: {currentTechnician}</div>}
    </div>
  )
  const statusSelect = () => (
    <div className="field">
      <label>Status</label>
      <select value={normaliseJobStatus(form.status)} onChange={e => set('status', e.target.value)}>
        {filterLifecycleStatusOptionsForProfile(JOB_STATUS_OPTIONS, profile, form.status).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
    </div>
  )
  const chk = (label, id) => (
    <label key={id} className="check-item">
      <input type="checkbox" checked={!!form[id]} onChange={e => setChk(id, e.target.checked)} />
      {' '}{label}
    </label>
  )

  function renderPartsUsedEditor() {
    return (
      <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Physical Parts & Materials</div>
            <div className="fs12 text-muted">Fitted or supplied items only. Service Kit recommendations and Bespoke Additional Quote stay separate.</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => addPartRow()}>+ Add Part</button>
        </div>

        {partRows.length === 0 ? (
          <div className="fs12 text-muted" style={{ padding: '8px 0' }}>No parts added yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {partRows.map((row, index) => (
              <div key={row.id || index} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, background: 'var(--panel)' }}>
                <div className="grid-3">
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>Part / Kit</label>
                    <input value={row.name || ''} onChange={e => updatePartRow(index, 'name', e.target.value)} placeholder="Service kit, seal head, upgrade part..." />
                  </div>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>Part Number</label>
                    <input value={row.part_number || ''} onChange={e => updatePartRow(index, 'part_number', e.target.value)} placeholder="00.4318..." />
                  </div>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>Supplier</label>
                    <input value={row.supplier || ''} onChange={e => updatePartRow(index, 'supplier', e.target.value)} placeholder="Zyrofisher, Madison..." />
                  </div>
                </div>
                <div className="grid-4">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Qty</label>
                    <input type="number" min="0" step="1" value={row.quantity ?? '1'} onChange={e => updatePartRow(index, 'quantity', e.target.value)} />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Cost Each (£)</label>
                    <input type="number" min="0" step="0.01" value={row.unit_cost ?? ''} onChange={e => updatePartRow(index, 'unit_cost', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Sell Each (£)</label>
                    <input type="number" min="0" step="0.01" value={row.unit_price ?? ''} onChange={e => updatePartRow(index, 'unit_price', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Line</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input readOnly value={`£${((Number(row.quantity || 0) || 0) * (Number(row.unit_price || 0) || 0)).toFixed(2)}`} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
                      <button className="btn btn-ghost btn-sm" onClick={() => removePartRow(index)} style={{ color: 'var(--red)', borderColor: 'var(--red)', flexShrink: 0 }}>Remove</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {partRows.length === 0 && legacyPartsCost > 0 && (
          <div className="alert alert-info" style={{ marginTop: 10, marginBottom: 0 }}>
            Existing legacy parts cost: £{legacyPartsCost.toFixed(2)}. Add line items above when you want this job to use structured parts.
          </div>
        )}

        <div className="grid-3" style={{ marginTop: 12 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Parts Cost Total (£)</label>
            <input readOnly value={`£${partsCostTotal.toFixed(2)}`} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Parts Sold Total (£)</label>
            <input readOnly value={`£${partsSoldTotal.toFixed(2)}`} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Parts Margin (£)</label>
            <input readOnly value={`£${partsMarginTotal.toFixed(2)}`} style={{ background: 'var(--black)', color: 'var(--green)', fontWeight: 700 }} />
          </div>
        </div>

        {isEdit && initialData?.id ? (
          <div style={{ marginTop: 12 }}>
            <JobStockUsagePanel
              vatInherited={inheritedServicePrices}
              jobId={initialData.id}
              workshopId={workshopId}
              mode="edit"
              sourceType="suspension_parts"
              compact
              onApplyToParts={addPartRow}
              onChanged={handleStockUsageChanged}
            />
          </div>
        ) : (
          <div className="field-hint" style={{ marginTop: 10 }}>Save the job before recording stock usage.</div>
        )}
      </div>
    )
  }

  function adjustmentStatusTone(status) {
    if (status === 'approved') return 'green'
    if (status === 'pending') return 'orange'
    return 'muted'
  }

  function renderPriceAdjustmentsEditor() {
    if(inheritedServicePrices)return <p className="text-muted">Discounts for this job are reviewed with its VAT totals above. Agreed prices remain protected.</p>
    const activeRows = normalisePriceAdjustments({ job_price_adjustments: priceAdjustments })
    const canAdd = isEdit && !adjustmentLocked
    return (
      <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Discounts &amp; Adjustments</div>
            <div className="fs12 text-muted">Approved rows affect totals. Pending rows are internal only.</div>
          </div>
          {adjustmentApprovalRequired && <StatusPill tone="orange">Owner approval required</StatusPill>}
        </div>
        {jobMeta.payment_refresh_required && (
          <div className="alert alert-info" style={{ marginBottom: 12 }}>
            {jobMeta.payment_refresh_reason || 'Pricing changed. Generate a new payment link before requesting payment.'}
          </div>
        )}
        {loyaltyProgress.enabled && isEdit && (
          <div className="alert alert-info" style={{ marginBottom: 12 }}>
            <div className="fw7 text-white">Loyalty Rewards</div>
            <div className="fs12" style={{ marginTop: 4 }}>
              {loyaltyProgress.availableCount > 0
                ? `${loyaltyProgress.availableCount} reward${loyaltyProgress.availableCount === 1 ? '' : 's'} available: ${loyaltyProgress.rewardLabel}.`
                : `${loyaltyProgress.qualifiedCount}/${loyaltyProgress.required} completed paid eligible services. ${loyaltyRuleSummary(effectiveLoyaltyWorkshop)}.`}
            </div>
            {loyaltyProgress.availableCount > 0 && (
              <div className="action-row" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-green btn-sm"
                  onClick={applyLoyaltyReward}
                  disabled={loyaltyBusy || loyaltyAlreadyAppliedToJob || !loyaltyEligibleJob || !canApplyLoyaltyByRole || adjustmentLocked}
                >
                  {loyaltyBusy ? 'Applying...' : 'Apply Loyalty Reward'}
                </button>
                {loyaltyAlreadyAppliedToJob && <span className="fs11 text-muted">Already applied to this job.</span>}
                {!loyaltyEligibleJob && <span className="fs11 text-muted">This job is not in an eligible loyalty category.</span>}
                {!canApplyLoyaltyByRole && <span className="fs11 text-muted">Owner required for this workshop.</span>}
              </div>
            )}
          </div>
        )}
        {activeRows.length > 0 && (
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {activeRows.map(row => {
              const amount = priceAdjustmentAmount(row, financials.subtotal)
              const sign = row.kind === 'discount' ? '-' : '+'
              const target = row.target_scope === 'job'
                ? 'Job subtotal'
                : (row.target_lines || []).map(line => line.name).filter(Boolean).join(', ') || 'Selected lines'
              return (
                <div key={row.id || `${row.kind}-${row.value}-${row.status}`} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div className="text-white fw7">{row.label || (row.kind === 'discount' ? 'Discount' : 'Adjustment')}</div>
                      <div className="fs11 text-muted">{target}</div>
                      {row.reason && <div className="fs11 text-muted" style={{ marginTop: 3 }}>Internal note: {row.reason}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className={row.kind === 'discount' ? 'text-white fw7' : 'text-green fw7'}>{sign} GBP {amount.toFixed(2)}</div>
                      <StatusPill tone={adjustmentStatusTone(row.status)}>{row.status}</StatusPill>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {!isEdit && <div className="alert alert-info" style={{ marginBottom: 0 }}>Save the job before adding discounts or adjustments.</div>}
        {adjustmentLocked && <div className="alert alert-info" style={{ marginBottom: 0 }}>Paid jobs are locked. Discounts and adjustments cannot be changed.</div>}
        {canAdd && (
          <>
            {adjustmentNotice && <div className="alert alert-success" style={{ marginBottom: 12 }}>{adjustmentNotice}</div>}
            {adjustmentError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{adjustmentError}</div>}
            <div className="grid-3">
              <div className="field">
                <label>Type</label>
                <select value={adjustmentForm.kind} onChange={e => setAdjustmentField('kind', e.target.value)}>
                  <option value="discount">Discount</option>
                  <option value="surcharge">Positive adjustment</option>
                </select>
              </div>
              <div className="field">
                <label>Value Type</label>
                <select value={adjustmentForm.value_type} onChange={e => setAdjustmentField('value_type', e.target.value)}>
                  <option value="fixed">Fixed GBP</option>
                  <option value="percent">Percent</option>
                </select>
              </div>
              <div className="field">
                <label>{adjustmentForm.value_type === 'percent' ? 'Value (%)' : 'Value (GBP)'}</label>
                <input type="number" min="0" step="0.01" value={adjustmentForm.value} onChange={e => setAdjustmentField('value', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Customer Label</label>
                <input value={adjustmentForm.label} onChange={e => setAdjustmentField('label', e.target.value)} placeholder={adjustmentForm.kind === 'discount' ? 'Discount' : 'Adjustment'} />
              </div>
              <div className="field">
                <label>Target</label>
                <select value={adjustmentForm.target_scope} onChange={e => setAdjustmentForm(f => ({ ...f, target_scope: e.target.value, target_line_ids: [] }))}>
                  <option value="selected_service_lines">Selected service/add-on lines</option>
                  <option value="job">Whole job subtotal</option>
                </select>
              </div>
            </div>
            {adjustmentForm.target_scope !== 'job' && (
              <div style={{ marginBottom: 12 }}>
                <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Applies to</div>
                {targetServiceLines.length ? targetServiceLines.map(line => (
                  <label key={line.service_id} className="check-item" style={{ marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={adjustmentForm.target_line_ids.includes(line.service_id)}
                      onChange={() => toggleAdjustmentLine(line.service_id)}
                    />
                    <span>{line.name}{line.is_addon ? ' (add-on)' : ''} - GBP {Number(line.line_total || 0).toFixed(2)}</span>
                  </label>
                )) : <div className="fs12 text-muted">Select services before targeting service lines.</div>}
              </div>
            )}
            <div className="field">
              <label>Internal Reason / Note <span className="text-muted" style={{ textTransform: 'none', fontWeight: 400 }}>(optional, not shown to customers)</span></label>
              <textarea value={adjustmentForm.reason} onChange={e => setAdjustmentField('reason', e.target.value)} style={{ minHeight: 56 }} placeholder="Goodwill discount, extra contamination surcharge, customer agreement, etc." />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div className="fs12 text-muted">
                Preview: <span className={adjustmentForm.kind === 'discount' ? 'text-white fw7' : 'text-green fw7'}>
                  {adjustmentForm.kind === 'discount' ? '-' : '+'} GBP {adjustmentPreview.toFixed(2)}
                </span>
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={createPriceAdjustment} disabled={adjustmentSaving}>
                {adjustmentSaving ? 'Saving...' : (adjustmentApprovalRequired && !isOwner ? 'Propose Adjustment' : 'Apply Adjustment')}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  const scopeFieldVisible = id => templateFieldVisible('scope', id, visibleScopeFields)
  const settingFieldIncluded = key => templateFieldVisible('settings', key, visibleSettingsFields)
  const settingFieldVisible = key => settingFieldIncluded(key) && !templateFieldOptional('settings', key)
  const serviceLogFieldVisible = key => templateFieldVisible('service_log', key, visibleServiceLogFields)
  const inspectionFieldIncluded = id => templateFieldVisible('inspection', id, visibleInspectionFields)
  const qcFieldIncluded = id => templateFieldVisible('qc', id, visibleQcFields)
  const handoverFieldVisible = id => templateFieldVisible('handover', id, visibleHandoverFields.length ? visibleHandoverFields : HANDOVER_FIELDS)

  function renderCheckGrid(fields, group, visibleFn) {
    const visible = fields.filter(([, id]) => visibleFn(id))
    const normal = visible.filter(([, id]) => !templateFieldOptional(group, id))
    const optional = visible.filter(([, id]) => templateFieldOptional(group, id))

    return (
      <>
        {normal.length > 0 && (
          <div className="grid-3">
            {chunks(normal, Math.ceil(normal.length / 3) || 1).map((chunk, idx) => (
              <div key={idx} className="checklist">
                {chunk.map(([label, id]) => chk(label, id))}
              </div>
            ))}
          </div>
        )}
        {optional.length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Optional / advanced checks</summary>
            <div className="grid-3" style={{ marginTop: 10 }}>
              {chunks(optional, Math.ceil(optional.length / 3) || 1).map((chunk, idx) => (
                <div key={idx} className="checklist">
                  {chunk.map(([label, id]) => chk(label, id))}
                </div>
              ))}
            </div>
          </details>
        )}
      </>
    )
  }

  function renderSettingsColumn(prefix, title, color, optionalOnly = false) {
    const fields = SETTINGS_FIELDS.filter(field =>
      settingFieldIncluded(field.key) && templateFieldOptional('settings', field.key) === optionalOnly
    )

    if (!fields.length) return null

    return (
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>{title}</div>
        {['Spring', 'Compression', 'Rebound'].map(group => {
          const groupFields = fields.filter(field => field.group === group)
          if (!groupFields.length) return null
          return (
            <React.Fragment key={group}>
              {sh(group, color)}
              {groupFields.map(field => fi(field.label, prefix === 'b' ? field.before : field.after, 'text', field.placeholder))}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  function measurementValue(field, side = null) {
    if (side === 'before' && field.before) return form[field.before] || ''
    if (side === 'after' && field.after) return form[field.after] || ''
    if (side && field.kind === 'before_after') {
      const measurements = normaliseObject(form.structured_measurements)
      return measurements[`${field.key}_${side}`] || ''
    }
    if (field.field) return form[field.field] || ''
    return normaliseObject(form.structured_measurements)[field.key] || ''
  }

  function setMeasurementValue(field, value, side = null) {
    if (side === 'before' && field.before) return set(field.before, value)
    if (side === 'after' && field.after) return set(field.after, value)
    if (side && field.kind === 'before_after') {
      return setForm(f => ({
        ...f,
        structured_measurements: {
          ...normaliseObject(f.structured_measurements),
          [`${field.key}_${side}`]: value,
        },
      }))
    }
    if (field.field) return set(field.field, value)
    setForm(f => ({
      ...f,
      structured_measurements: {
        ...normaliseObject(f.structured_measurements),
        [field.key]: value,
      },
    }))
  }

  function fieldLabel(field) {
    const unit = field.unit ? ` (${field.unit})` : ''
    return `${field.label || field.key}${unit}`
  }

  function renderMeasurementInput(field, value, onChange, placeholder = '') {
    if (field.input === 'textarea') {
      return <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ minHeight: 72 }} />
    }
    return (
      <input
        type={field.input || 'text'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        step={field.step || undefined}
      />
    )
  }

  function renderMeasurementField(field) {
    if (field.field === 'kit') return <React.Fragment key={field.key || field.field}>{renderKitField()}</React.Fragment>
    const key = field.key || field.field || field.label
    const gridColumn = field.input === 'textarea' || field.kind === 'before_after' ? 'span 2' : undefined

    if (field.kind === 'before_after') {
      return (
        <div key={key} className="field" style={{ gridColumn }}>
          <label>{fieldLabel(field)}</label>
          <div className="grid-2" style={{ gap: 8 }}>
            <input
              value={measurementValue(field, 'before')}
              onChange={e => setMeasurementValue(field, e.target.value, 'before')}
              placeholder={field.before_placeholder || 'Before'}
            />
            <input
              value={measurementValue(field, 'after')}
              onChange={e => setMeasurementValue(field, e.target.value, 'after')}
              placeholder={field.after_placeholder || 'After'}
            />
          </div>
        </div>
      )
    }

    return (
      <div key={key} className="field" style={{ gridColumn }}>
        <label>{fieldLabel(field)}</label>
        {renderMeasurementInput(
          field,
          measurementValue(field),
          value => setMeasurementValue(field, value),
          field.placeholder || ''
        )}
      </div>
    )
  }

  function renderMeasurementSection(section) {
    const visibleFields = section.fields.filter(field => !field.advanced && field.visibility !== 'internal')
    const advancedFields = section.fields.filter(field => field.advanced || field.visibility === 'internal')

    return (
      <div key={section.key} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 12, background: 'var(--black)' }}>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: section.description ? 4 : 12 }}>
          {section.title}
        </div>
        {section.description && <div className="fs12 text-muted" style={{ marginBottom: 12 }}>{section.description}</div>}
        {visibleFields.length > 0 && (
          <div className="grid-4">
            {visibleFields.map(renderMeasurementField)}
          </div>
        )}
        {advancedFields.length > 0 && (
          <details style={{ marginTop: visibleFields.length ? 8 : 0 }}>
            <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Advanced / internal measurements</summary>
            <div className="grid-4" style={{ marginTop: 12 }}>
              {advancedFields.map(renderMeasurementField)}
            </div>
          </details>
        )}
      </div>
    )
  }

  function renderMeasurementSections(sections, emptyText) {
    if (!sections.length) return <div className="fs12 text-muted" style={{ marginBottom: 12 }}>{emptyText}</div>
    return sections.map(renderMeasurementSection)
  }

  function setSectionOpen(key, open) {
    setOpenSections(sections => ({ ...sections, [key]: open }))
  }

  function renderJobSection(key, title, summary, children) {
    const sectionTab = SUSPENSION_SECTION_TABS[key]
    if (isEdit && !visitedWorkspaceTabs.has(sectionTab)) return null
    const isTabHidden = isEdit && sectionTab !== workspaceTab
    const progress = sectionProgress(key)
    const isOpen = !!openSections[key]
    const subtitle = key === 'quote' && inheritedServicePrices ? 'Recorded service prices and customer approval.' : SUSPENSION_SECTION_HELP[key]
    return (
      <details
        className={`job-card-foldout suspension-job-foldout${isOpen ? ' open' : ''}`}
        hidden={isTabHidden}
        open={isOpen}
        onToggle={event => setSectionOpen(key, event.currentTarget.open)}
      >
        <summary className="job-card-foldout-head">
          <span>
            <strong>{title}</strong>
            {subtitle && <small>{subtitle}</small>}
          </span>
          <span className="job-card-foldout-meta">
            {progress && <StatusPill as="em" tone={progress.tone}>{progress.label}</StatusPill>}
            {summary && <small>{summary}</small>}
            <b>{isOpen ? 'Hide' : 'Show'}</b>
          </span>
        </summary>
        <div className="job-card-foldout-body">
          {children}
        </div>
      </details>
    )
  }

  function serviceLogFieldLabel(field) {
    if (field.key !== 'aircan_grease') return field.label
    const type = String(activeTemplate?.component_type || selectedComponent?.type || '').toLowerCase()
    if (type === 'fork') return 'Air Spring Grease'
    if (type === 'shock') return 'Air Can Grease'
    if (type === 'dropper') return 'Seatpost Grease / Lubricant'
    return 'Grease / Lubricant Used'
  }

  function renderKitField() {
    return (
      <div key="kit" style={{ gridColumn: 'span 2' }}>
        {selectedComponent && (
          <div style={{ marginBottom: 14 }}>
            {kitRecs.length > 0 ? (
              <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 10 }}>
                  Kit Recommendations - {kitContextLabel}
                </div>
                {kitRecs.map(kit => {
                  const price = kit.kit_prices?.[0]
                  const applied = kitIsApplied(kit)
                  return (
                    <div key={kit.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)', fontFamily: 'monospace' }}>{kit.part_number}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{kit.description}</div>
                        {price && (
                          <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 2 }}>
                            £{Number(price.unit_cost).toFixed(2)}{price.supplier ? ` - ${price.supplier}` : ''}
                          </div>
                        )}
                        {renderKitRecommendationMeta(kit)}
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => applyKit(kit)} style={{ flexShrink: 0 }}>
                        {applied ? 'Reapply kit' : 'Use this kit'}
                      </button>
                    </div>
                  )
                })}
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, fontSize: 11 }} onClick={() => setShowAddKit(s => !s)}>
                  + Add different kit
                </button>
              </div>
            ) : (
              <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                  No matching kit on record for <span style={{ color: 'var(--text-2)', fontFamily: modelCode ? 'monospace' : 'inherit' }}>{kitContextLabel}</span>.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {trailheadKitEligible && (
                    <button className="btn btn-primary btn-sm" onClick={findTrailheadServiceKit} disabled={trailheadKitState === 'loading'}>
                      {trailheadKitState === 'loading' ? 'Checking Trailhead...' : 'Check Trailhead'}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAddKit(s => !s)}>+ Add service kit</button>
                </div>
                {trailheadKitMsg && (
                  <div style={{ fontSize: 11, color: trailheadKitState === 'error' ? 'var(--red)' : 'var(--green)', marginTop: 8 }}>
                    {trailheadKitMsg}
                  </div>
                )}
              </div>
            )}

            {showAddKit && (
              <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 12 }}>Add kit to shared library</div>
                <div className="grid-2" style={{ marginBottom: 10 }}>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Part Number</label>
                    <input value={newKit.part_number} onChange={e => setNewKit(k => ({ ...k, part_number: e.target.value }))} placeholder="00.4318.037.017" />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Your Cost (£)</label>
                    <input type="number" value={newKit.unit_cost} onChange={e => setNewKit(k => ({ ...k, unit_cost: e.target.value }))} placeholder="19.82" step="0.01" />
                  </div>
                </div>
                <div className="grid-2" style={{ marginBottom: 10 }}>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Description</label>
                    <input value={newKit.description} onChange={e => setNewKit(k => ({ ...k, description: e.target.value }))} placeholder="Vivid Coil Ultimate Service Kit" />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Supplier</label>
                    <input value={newKit.supplier} onChange={e => setNewKit(k => ({ ...k, supplier: e.target.value }))} placeholder="Zyrofisher" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveNewKit} disabled={savingKit || !newKit.part_number || !newKit.description}>
                    {savingKit ? 'Saving...' : 'Save & apply'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAddKit(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
        {fi('Service Kit Used', 'kit', 'text', 'e.g. 00.4318.037.017 - Vivid Coil Service Kit')}
      </div>
    )
  }

  function renderServiceLogField(field) {
    if (field.key === 'kit') return renderKitField()
    return <React.Fragment key={field.key}>{fi(serviceLogFieldLabel(field), field.key, 'text', field.placeholder)}</React.Fragment>
  }

  function renderAdditionalServiceInformation() {
    return (
      <details style={{ marginTop: 8 }}>
        <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Additional Service Information</summary>
        <div style={{ ...sectionPanelStyle, marginTop: 12, marginBottom: 0 }}>
          <div className="grid-3">
            {ADDITIONAL_SERVICE_INFO_FIELDS.map(field => (
              <React.Fragment key={field.key}>
                {fi(field.label, field.key, 'text', field.placeholder)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </details>
    )
  }

  const serviceKitMeasurementSections = measurementServiceLogSections
    .map(section => ({
      ...section,
      fields: section.fields.filter(field => SERVICE_KIT_FIELD_KEYS.has(field.field || field.key)),
    }))
    .filter(section => section.fields.length > 0)
  const normalServiceLogFields = SERVICE_LOG_FIELDS.filter(field =>
    SERVICE_KIT_FIELD_KEYS.has(field.key) && serviceLogFieldVisible(field.key) && !templateFieldOptional('service_log', field.key)
  )
  const optionalServiceLogFields = SERVICE_LOG_FIELDS.filter(field =>
    SERVICE_KIT_FIELD_KEYS.has(field.key) && serviceLogFieldVisible(field.key) && templateFieldOptional('service_log', field.key)
  )
  const hasMainSettings = SETTINGS_FIELDS.some(field => settingFieldVisible(field.key))
  const hasOptionalSettings = SETTINGS_FIELDS.some(field => settingFieldIncluded(field.key) && templateFieldOptional('settings', field.key))
  const selectedScopeCount = SCOPE_FIELDS.filter(id => scopeFieldVisible(id) && form[id]).length
  const visibleScopeCount = SCOPE_FIELDS.filter(scopeFieldVisible).length
  const settingsValueCount = SETTINGS_FIELDS.filter(field =>
    String(form[field.before] || '').trim() || String(form[field.after] || '').trim()
  ).length
  const additionalServiceInfoCount = ADDITIONAL_SERVICE_INFO_FIELDS.filter(field => String(form[field.key] || '').trim()).length
  const serviceKitCount = ['kit', 'spring_hw'].filter(key => String(form[key] || '').trim()).length
  const serviceLogSummary = form.kit
    ? `Kit recorded: ${form.kit}`
    : activeTemplate
      ? 'Template fields only'
      : 'Kit and hardware'
  const qcSummary = form.qc_signed_off_at
    ? `Signed off${form.qc_status ? ` - ${form.qc_status}` : ''}`
    : 'Pending sign-off'
  const quoteSectionSummary = quoteStatusLabel === 'Not sent'
    ? 'One-off work outside menu'
    : quoteStatusLabel

  function sectionProgress(key) {
    const complete = { label: 'Complete', tone: 'green' }
    const inProgress = { label: 'In progress', tone: 'muted' }
    if (key === 'scope') return selectedScopeCount > 0 ? complete : inProgress
    if (key === 'settings') return (settingsValueCount + additionalServiceInfoCount) > 0 ? complete : inProgress
    if (key === 'inspection') return form.inspection_completed_at ? complete : inProgress
    if (key === 'serviceLog') return serviceKitCount > 0 ? complete : inProgress
    if (key === 'extraServices') return approvedExtraServiceRows.length > 0 ? complete : extraServiceLineRows.length > 0 ? inProgress : null
    if (key === 'quote') return quoteStatusLabel === 'Waiting approval' ? inProgress : complete
    if (key === 'evidence') return isEdit ? complete : inProgress
    if (key === 'qc') return form.qc_signed_off_at ? complete : inProgress
    if (key === 'handover') return ['interval','next_service','next_service_date','further_work','worn_parts','setup_rec'].some(field => String(form[field] || '').trim()) ? complete : inProgress
    if (key === 'stockMaterials') return partRows.length > 0 || stockUsageRows.length > 0 ? complete : null
    if (key === 'financials') return Number(displayedTotal || 0) > 0 ? complete : inProgress
    if (key === 'notes') return String(form.notes || '').trim() ? complete : null
    return null
  }

  const templateServiceLogSection = (
    renderJobSection('serviceLog', 'Service Kit', serviceLogSummary, (
      serviceKitMeasurementSections.length > 0 ? (
        renderMeasurementSections(serviceKitMeasurementSections, 'No service kit fields are required for this template.')
      ) : (
        <div style={sectionPanelStyle}>
          {normalServiceLogFields.length > 0 ? (
            <div className="grid-4">
              {normalServiceLogFields.map(renderServiceLogField)}
            </div>
          ) : (
            <div className="fs12 text-muted" style={{ marginBottom: 12 }}>No service kit fields are required for this template.</div>
          )}
          {optionalServiceLogFields.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Optional / advanced service kit fields</summary>
              <div className="grid-4" style={{ marginTop: 12 }}>
                {optionalServiceLogFields.map(renderServiceLogField)}
              </div>
            </details>
          )}
        </div>
      )
    ))
  )
  const serviceLogSection = (
    <>
      <hr />
        <div className="sec">Service Kit</div>
      <div className="grid-4">
        <div>
          {selectedComponent && (
            <div style={{ marginBottom: 14 }}>
              {kitRecs.length > 0 ? (
                <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 10 }}>
                    Kit Recommendations — {kitContextLabel}
                  </div>
                  {kitRecs.map(kit => {
                    const price = kit.kit_prices?.[0]
                    const applied = kitIsApplied(kit)
                    return (
                      <div key={kit.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)', fontFamily: 'monospace' }}>{kit.part_number}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{kit.description}</div>
                          {price && (
                            <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 2 }}>
                              £{Number(price.unit_cost).toFixed(2)}{price.supplier ? ` · ${price.supplier}` : ''}
                            </div>
                          )}
                          {renderKitRecommendationMeta(kit)}
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => applyKit(kit)}
                          style={{ flexShrink: 0 }}
                        >
                          {applied ? 'Reapply kit' : 'Use this kit'}
                        </button>
                      </div>
                    )
                  })}
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: 10, fontSize: 11 }}
                    onClick={() => setShowAddKit(s => !s)}
                  >
                    + Add different kit
                  </button>
                </div>
              ) : (
                <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                    No matching kit on record for <span style={{ color: 'var(--text-2)', fontFamily: modelCode ? 'monospace' : 'inherit' }}>{kitContextLabel}</span>.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {trailheadKitEligible && (
                      <button className="btn btn-primary btn-sm" onClick={findTrailheadServiceKit} disabled={trailheadKitState === 'loading'}>
                        {trailheadKitState === 'loading' ? 'Checking Trailhead...' : 'Check Trailhead'}
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowAddKit(s => !s)}>+ Add service kit</button>
                  </div>
                  {trailheadKitMsg && (
                    <div style={{ fontSize: 11, color: trailheadKitState === 'error' ? 'var(--red)' : 'var(--green)', marginTop: 8 }}>
                      {trailheadKitMsg}
                    </div>
                  )}
                </div>
              )}

              {showAddKit && (
                <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 12 }}>Add kit to shared library</div>
                  <div className="grid-2" style={{ marginBottom: 10 }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Part Number</label>
                      <input value={newKit.part_number} onChange={e => setNewKit(k => ({ ...k, part_number: e.target.value }))} placeholder="00.4318.037.017" />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Your Cost (£)</label>
                      <input type="number" value={newKit.unit_cost} onChange={e => setNewKit(k => ({ ...k, unit_cost: e.target.value }))} placeholder="19.82" step="0.01" />
                    </div>
                  </div>
                  <div className="grid-2" style={{ marginBottom: 10 }}>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Description</label>
                      <input value={newKit.description} onChange={e => setNewKit(k => ({ ...k, description: e.target.value }))} placeholder="Vivid Coil Ultimate Service Kit" />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Supplier</label>
                      <input value={newKit.supplier} onChange={e => setNewKit(k => ({ ...k, supplier: e.target.value }))} placeholder="Zyrofisher" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveNewKit} disabled={savingKit || !newKit.part_number || !newKit.description}>
                      {savingKit ? 'Saving…' : 'Save & apply'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowAddKit(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {fi('Service Kit Used', 'kit', 'text', 'e.g. 00.4318.037.017 — Vivid Coil Service Kit')}
        </div>
        {fi('Spring Hardware / Reducer Fitted', 'spring_hw', 'text', 'e.g. 50mm reducer')}
      </div>
    </>
  )

  return (
    <div className="modal-overlay">
      <div className={`modal ${isEdit ? 'job-workspace-modal suspension-workspace' : 'modal-wide'}`}>
        {isEdit ? (
          <>
            <header className="suspension-workspace-header">
              <div className="suspension-workspace-topline">
                <button type="button" className="suspension-workspace-back" onClick={onClose}>← {returnLabel}</button>
                <div className="suspension-workspace-actions">
                  {initialData?.id && <a className="btn btn-ghost btn-sm" href={`/jobs/${initialData.id}/time`} target="_blank" rel="noreferrer" aria-label="Actual time, opens in a new tab">Actual time ↗</a>}
                  <StatusPill tone={statusMeta.tone}>{statusMeta.label}</StatusPill>
                  <StatusPill tone={paymentMeta.tone}>Payment: {paymentMeta.label}</StatusPill>
                  <span role="status" className="job-save-status">{!saving && !error && savedForm?.form === form && savedForm?.selectedServices === selectedServices ? 'Changes saved.' : ''}</span>
                  <button type="button" className="btn btn-primary" onClick={save} disabled={saving || deleting || vatIntake.blocked}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button type="button" className="close-btn" aria-label="Close job" onClick={onClose}>×</button>
                </div>
              </div>
              <div className="suspension-workspace-title-row">
                <div>
                  <div className="suspension-workspace-kicker">Suspension job {initialData?.job_number ? `· ${initialData.job_number}` : ''}</div>
                  {process.env.REACT_APP_VAT_BUILD2 === 'true' && profile?.role === 'owner' && <a href={`/vat-documents/${initialData.id}`}>Prepare financial document</a>}
                  <h2>{selectedComponentSummary}</h2>
                  <p>{selectedCustomerSummary} · {selectedServiceScopeSummary}</p>
                </div>
                <div className="suspension-workspace-next">
                  <span>Next action</span>
                  <strong>{nextAction.title}</strong>
                  <small>{nextAction.detail}</small>
                </div>
              </div>
              <div className="suspension-workspace-progress" aria-label="Job progress">
                {progressItems.map(item => (
                  <StatusPill key={item.key} tone={item.tone}>
                    {item.label}: {item.value}
                  </StatusPill>
                ))}
              </div>
            </header>
            <nav className="suspension-workspace-tabs" aria-label="Suspension job sections">
              {SUSPENSION_WORKSPACE_TABS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={workspaceTab === key ? 'is-active' : ''}
                  aria-current={workspaceTab === key ? 'page' : undefined}
                  onClick={() => {
                    setWorkspaceTab(key)
                    setVisitedWorkspaceTabs(tabs => new Set(tabs).add(key))
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
          </>
        ) : <div className="modal-header">
          <div>
            <div className="modal-title">New Service Job</div>
            {form.customer_id && (() => {
              const cust = customers.find(c => c.id === form.customer_id)
              const comp = components.find(c => c.id === form.component_id)
              if (!cust) return null
              const customerName = `${cust.first_name} ${cust.last_name}`.trim()
              const componentName = comp ? `${comp.year ? comp.year + ' ' : ''}${comp.model}` : ''
              return (
                <div className="text-muted fs12" style={{ marginTop: 3, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {isEdit ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={openCustomerFromJob}
                      disabled={customerNavSaving || saving}
                      style={{ padding: '2px 7px', minHeight: 0 }}
                    >
                      {customerNavSaving ? 'Saving...' : customerName}
                    </button>
                  ) : (
                    <span>{customerName}</span>
                  )}
                  {componentName && <span>- {componentName}</span>}
                </div>
              )
            })()}
          </div>
          <button className="close-btn" onClick={() => { if (!isEdit) clearDraft(); onClose() }}>×</button>
        </div>}

        {hasDraft && !isEdit && (
          <div className="alert alert-info" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📋 Draft restored — your work was saved automatically.</span>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              clearDraft()
              setForm(buildInitialForm(null, preCustomerId, preComponentId))
              setHasDraft(false)
            }}>Start fresh</button>
          </div>
        )}

        {error && <div className="alert alert-error suspension-workspace-error" role="alert" style={{ marginBottom: 14 }}><strong>Save failed.</strong> {error}</div>}

        {isEdit && !form.component_id && (
          <div className="alert alert-info" style={{ marginBottom: 14 }}>
            <div className="fw7" style={{ marginBottom: existingServiceSummary ? 6 : 0 }}>Booking job without a linked component</div>
            {existingServiceSummary && <div className="fs12">Selected services: {existingServiceSummary}</div>}
            <div className="fs12" style={{ marginTop: existingServiceSummary ? 6 : 0 }}>Create the component from the job view, then assign it here when ready.</div>
          </div>
        )}

        {(!isEdit || workspaceTab === 'admin') && <>
        <div className="sec" style={{ marginTop: 0 }}>Job details</div>
        <div style={sectionPanelStyle}>

        {shouldShowTargetSelectors && (
          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Customer</label>
              <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
                <option value="">Select customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Component</label>
              <select value={form.component_id} onChange={e => {
                const nextComponent = components.find(c => c.id === e.target.value)
                setForm(f => ({ ...f, component_id: e.target.value, bike_id: nextComponent?.bike_id || f.bike_id || '' }))
              }} disabled={!form.customer_id}>
                <option value="">Select component…</option>
                {custComponents.map(c => <option key={c.id} value={c.id}>{c.year ? c.year+' ':''}{c.model} {c.serial ? '('+c.serial+')' : ''}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="job-modal-summary">
          <div className="job-modal-summary-item">
            <span>Customer</span>
            <strong title={selectedCustomerSummary}>{selectedCustomerSummary}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Component</span>
            <strong title={selectedComponentSummary}>{selectedComponentSummary}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Selected service</span>
            <strong title={selectedServiceScopeSummary}>{selectedServiceScopeSummary}</strong>
          </div>
          <div className="job-modal-summary-item green">
            <span>Job total</span>
            <strong>£{displayedTotal.toFixed(2)}</strong>
          </div>
        </div>

        <div className="grid-3">
          {fi('Received Date', 'date', 'date')}
          {fi('Date Booked In For', 'scheduled_date', 'date')}
          {technicianSelect()}
          <div className="field">
            <label>Planned Time</label>
            <div className="detail-val" style={{ minHeight: 38, display: 'flex', alignItems: 'center' }}>
              {plannedScheduleMinutes ? formatPlannedMinutes(plannedScheduleMinutes) : 'Time required'}
            </div>
            <div className="field-hint">From selected service settings.</div>
          </div>
        </div>
        {canOverrideSchedule && (
          <label className="check-item" style={{ marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={scheduleOverrideOverCapacity}
              onChange={e => setScheduleOverrideOverCapacity(e.target.checked)}
            />
            Allow over-capacity schedule save
          </label>
        )}
        <div className="grid-3">
          {statusSelect()}
          {fi('Date For Collection / Returned', 'due_date', 'date')}
          {fi('Start Time (24hr)', 'start_time', 'time')}
          {fi('Finish Time (24hr)', 'finish_time', 'time')}
        </div>
        <div className="field">
          <label>Physical Location</label>
          <input value={form.physical_location || ''} onChange={e => set('physical_location', e.target.value)} placeholder="Rack A, bench 2, awaiting wash..." />
        </div>
        {duration && (
          <div className="duration-display" style={{ marginBottom: 16 }}>
            <span className="text-muted">Duration: </span>
            <span className="text-white fw7">{fmtDuration(duration)}</span>
            <span className="text-muted"> ({form.start_time} → {form.finish_time})</span>
          </div>
        )}

        {isEdit && (
          <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <div className="job-modal-callout-title">Current Stage</div>
                <div className="fs12 text-muted" style={{ marginTop: 8 }}>
                  <StatusPill tone={statusMeta.tone} style={{ marginRight: 8 }}>{statusMeta.label}</StatusPill>
                  <StatusPill tone={paymentMeta.tone}>Payment: {paymentMeta.label}</StatusPill>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 5 }}>Next action</div>
                <div className="text-white fw7" style={{ lineHeight: 1.35 }}>{nextAction.title}</div>
                <div className="fs12 text-muted" style={{ marginTop: 3, lineHeight: 1.45 }}>{nextAction.detail}</div>
              </div>
            </div>
            <div className="action-row" style={{ marginTop: 10 }}>
              {progressItems.map(item => (
                <StatusPill key={item.key} tone={item.tone}>
                  {item.label}: {item.value}
                </StatusPill>
              ))}
            </div>
            <details style={{ marginTop: 12 }}>
              <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Show job timeline</summary>
              <div style={{ marginTop: 10 }}>
                <JobTimeline
                  job={timelineJob}
                  customer={selectedCustomer}
                  component={selectedComponent}
                  compact
                />
              </div>
            </details>
          </div>
        )}
        {workflowNotice && <div className="alert alert-info" style={{ marginTop: 12 }}>{workflowNotice}</div>}
        {inheritedServicePrices && process.env.REACT_APP_VAT_BUILD3 === 'true' && <VatJobWorkflow job={initialData} profile={profile} customer={selectedCustomer} allowQuote={false} onDiscountApplied={async()=>{const refreshed=await reloadJobPricing();if(onChanged)onChanged(refreshed)}} />}
        {workflowError && <div className="alert alert-error" style={{ marginTop: 12 }}>{workflowError}</div>}
        </div>
        </>}

        {(!isEdit || workspaceTab === 'work') && services.length > 0 && (() => {
          const comp = components.find(c => c.id === form.component_id)
          const relevantServices = suspensionServiceRows.filter(s =>
            s.active !== false &&
            (!comp || s.category === comp.type || s.category === 'combined' || s.category === 'other') &&
            (s.brand === 'any' || !comp || s.brand === comp.brand)
          )
          if (!relevantServices.length) return null
          const baseServices = relevantServices.filter(s => !s.is_addon)
          const addonServices = relevantServices.filter(s => s.is_addon)
          const selectedServiceTotal = inheritedServicePrices ? Number(initialData.labour_charge || 0) : selectedServiceRowsForSummary.reduce((sum, s) => sum + Number(s.base_price || 0), 0)
          return (
            <div className="job-modal-callout">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: serviceMenuOpen ? 12 : 0 }}>
                <div>
                  <div className="job-modal-callout-title">Service Menu</div>
                  <div className="fs12 text-muted">
                    {selectedServices.length
                      ? `${selectedServices.length} selected · £${(vatIntake.gross ?? selectedServiceTotal).toFixed(2)}`
                      : 'Choose the service or services for this job.'}
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setServiceMenuOpen(open => !open)}>
                  {serviceMenuOpen ? 'Collapse' : 'Change Service'}
                </button>
              </div>
              <VatIntakeTotals intake={vatIntake} />
              {serviceMenuOpen && (
                <>
                  {baseServices.length > 0 && (
                    <>
                      <div className="text-muted fs11 fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Base Services</div>
                      {baseServices.map(s => {
                        const checked = selectedServices.includes(s.id)
                        return (
                          <label key={s.id} className="check-item" style={{ marginBottom: 6 }} onClick={() => {
                            const total = checked
                              ? selectedServices.filter(id => id !== s.id)
                              : [...selectedServices.filter(id => !baseServices.find(bs => bs.id === id)), s.id]
                            setSelectedServices(total)
                            const newTotal = suspensionServiceRows.filter(sv => total.includes(sv.id)).reduce((sum, sv) => sum + Number(sv.base_price || 0), 0)
                            setServiceMenuTotal(newTotal)
                          }}>
                            <input type="checkbox" checked={checked} onChange={() => {}} />
                            <span style={{ flex: 1 }}>{s.name}</span>
                            <span className="text-green fw7">{jobServicePriceLabel(s, vatIntake, inheritedServicePrices ? normaliseJobServiceSnapshot(initialData?.service_snapshot) : [])}</span>
                          </label>
                        )
                      })}
                    </>
                  )}
                  {addonServices.length > 0 && (
                    <>
                      <div className="text-muted fs11 fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', margin: '10px 0 6px' }}>Add-ons</div>
                      {addonServices.map(s => {
                        const checked = selectedServices.includes(s.id)
                        return (
                          <label key={s.id} className="check-item" style={{ marginBottom: 6 }} onClick={() => {
                            const total = checked ? selectedServices.filter(id => id !== s.id) : [...selectedServices, s.id]
                            setSelectedServices(total)
                            const newTotal = suspensionServiceRows.filter(sv => total.includes(sv.id)).reduce((sum, sv) => sum + Number(sv.base_price || 0), 0)
                            setServiceMenuTotal(newTotal)
                          }}>
                            <input type="checkbox" checked={checked} onChange={() => {}} />
                            <span style={{ flex: 1 }}>{s.name}</span>
                            <span className="text-green fw7">+ {jobServicePriceLabel(s, vatIntake, inheritedServicePrices ? normaliseJobServiceSnapshot(initialData?.service_snapshot) : [])}</span>
                          </label>
                        )
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          )
        })()}

        {(!isEdit || workspaceTab === 'work') && approvedExtraServiceRows.length > 0 && (
          <div className="general-approved-extra-services">
            <div className="general-approved-extra-services-head">
              <span>Approved extra suspension services</span>
              <strong>{approvedExtraServiceRows.length} mid-job service{approvedExtraServiceRows.length === 1 ? '' : 's'}</strong>
            </div>
            <div className="general-approved-extra-service-list">
              {approvedExtraServiceRows.map(row => (
                <div key={row.id || `${row.name}-${row.category}`} className="general-approved-extra-service-chip">
                  <span>{row.name}</span>
                  {row.category && <em>{row.category}</em>}
                  {row.amount > 0 && <b>+ £{row.amount.toFixed(2)}</b>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(!isEdit || workspaceTab === 'work') && templateOptions.length > 0 && (
          <div className="job-modal-callout">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="job-modal-callout-title">Service Template</div>
                <div className="fs12 text-muted">
                  Apply a default fork, shock, dropper or setup workflow. Inspection and QC are shown as guidance, not pre-completed.
                </div>
              </div>
              {activeTemplate && (
                <button className="btn btn-ghost btn-sm" onClick={clearServiceTemplate}>Clear Template</button>
              )}
            </div>

            {templateNotice && <div className="alert alert-info" style={{ marginBottom: 12 }}>{templateNotice}</div>}

            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Apply Template</label>
                <select value={templateSelectValue} onChange={e => handleTemplateSelectChange(e.target.value)}>
                  <option value="">Choose a template...</option>
                  {templateSelectOptions.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.workshop_id ? ' - workshop' : ' - default'}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => applyServiceTemplate(templateOptions.find(template => template.id === templateChoice))}
                  disabled={!templateChoice}
                  style={{ marginBottom: 0 }}
                >
                  {activeTemplate ? 'Apply Change' : 'Apply Template'}
                </button>
              </div>
            </div>

            {activeTemplate && (
              <>
                <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Applied template</div>
                    <div className="fs12 text-white fw7">{activeTemplate.name}</div>
                    {activeAddons.length > 0 && (
                      <div className="fs11 text-muted" style={{ marginTop: 3 }}>
                        Add-ons: {activeAddons.map(addon => addon.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setTemplateDetailsOpen(open => !open)}>
                    {templateDetailsOpen ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
                {templateDetailsOpen && (
                  <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                      <div>
                        <div className="fs12 text-white fw7">{activeTemplate.name}</div>
                        {activeTemplate.description && <div className="fs12 text-muted" style={{ marginTop: 3 }}>{activeTemplate.description}</div>}
                      </div>
                      <span className="badge badge-other">{activeTemplate.component_type || 'template'}</span>
                    </div>
                    <div className="grid-3" style={{ gap: 12 }}>
                      <div>
                        <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Scope</div>
                        <div className="fs12 text-muted">{templateFieldLabels(activeTemplate.scope_fields).join(', ') || 'Manual scope'}</div>
                      </div>
                      <div>
                        <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Inspection Focus</div>
                        <div className="fs12 text-muted">{templateFieldLabels(activeTemplate.inspection_fields).join(', ') || 'Standard inspection'}</div>
                      </div>
                      <div>
                        <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>QC Focus</div>
                        <div className="fs12 text-muted">{templateFieldLabels(activeTemplate.qc_fields).join(', ') || 'Standard QC'}</div>
                      </div>
                    </div>
                    {addonOptions.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                        <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Compatible add-ons</div>
                        <div className="grid-2">
                          {addonOptions.map(addon => (
                            <label key={addon.id} className="check-item">
                              <input
                                type="checkbox"
                                checked={selectedAddonMatches(addon)}
                                onChange={e => toggleTemplateAddon(addon, e.target.checked)}
                              />
                              {' '}{addon.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {renderJobSection('scope', 'Service Scope', `${selectedScopeCount}/${visibleScopeCount || SCOPE_FIELDS.length} selected`, (
        <div style={sectionPanelStyle}>
          <div className="grid-3">
            <div className="checklist">
              {scopeFieldVisible('sc_lower_leg') && chk('Lower leg service', 'sc_lower_leg')}
              {scopeFieldVisible('sc_air_spring') && chk('Air spring service', 'sc_air_spring')}
              {scopeFieldVisible('sc_damper') && chk('Damper rebuild', 'sc_damper')}
              {scopeFieldVisible('sc_air_can') && chk('Air can service', 'sc_air_can')}
            </div>
            <div className="checklist">
              {scopeFieldVisible('sc_full_shock') && chk('Full shock service', 'sc_full_shock')}
              {scopeFieldVisible('sc_bushing') && chk('Bushing / hardware replacement', 'sc_bushing')}
              {scopeFieldVisible('sc_travel') && chk('Travel change', 'sc_travel')}
              {scopeFieldVisible('sc_seal_upgrade') && chk('Seal upgrade', 'sc_seal_upgrade')}
            </div>
            <div className="checklist">
              {scopeFieldVisible('sc_retune') && chk('Custom tune / revalve', 'sc_retune')}
              {scopeFieldVisible('sc_setup') && chk('Setup session only', 'sc_setup')}
              {scopeFieldVisible('sc_dropper_service') && chk('Seatpost / dropper service', 'sc_dropper_service')}
            </div>
          </div>
        </div>
        ))}

        {renderJobSection('extraServices', 'Extra Service Lines', approvedExtraServiceRows.length
          ? `${approvedExtraServiceRows.length} approved - £${extraServiceLineBillableTotal(extraServiceLineRows).toFixed(2)}`
          : extraServiceLineRows.length
            ? `${extraServiceLineRows.length} pending or draft`
            : 'Standard services found mid-job', (
          <GeneralExtraServiceLinePanel
            job={initialData}
            workshopId={workshopId}
            services={suspensionServiceRows}
            mode={isEdit ? 'edit' : 'view'}
            moduleKey="suspension"
            title="Extra Service Lines"
            serviceSelectLabel="Extra Suspension Service"
            introTitle="Standard suspension services found mid-job"
            introText="Use this for standard suspension menu services discovered after intake. Keep stock/materials, Service Kit, and Bespoke Additional Quote separate."
            savedBeforeAddingMessage="Save the suspension job before adding extra service-line approvals."
            emptyMessage="No extra suspension service lines have been recorded yet."
            customerEmail={selectedCustomer?.email || ''}
            onRowsChange={handleExtraServiceLineRowsChange}
            onChanged={handleExtraServiceLineChanged}
          />
        ))}

        {renderJobSection('settings', 'Setup Settings', activeTemplate ? 'Template-specific fields' : 'Baseline and return setup', (
          <>
            {measurementSettingSections.length > 0 ? (
              renderMeasurementSections(measurementSettingSections, 'No settings are required for this template.')
            ) : (
              <div style={sectionPanelStyle}>
                <div className="grid-2">
                  <div>
                    {hasMainSettings && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Before Service</div>}
                    {['air','sag','preload','tokens'].some(settingFieldVisible) && sh('Spring', 'var(--muted)')}
                    {settingFieldVisible('air') && fi('Air Pressure / Spring Rate', 'b_air', 'text', 'e.g. 75 psi')}
                    {settingFieldVisible('sag') && fi('Sag %', 'b_sag', 'text', 'e.g. 30%')}
                    {settingFieldVisible('preload') && fi('Coil Preload (turns)', 'b_preload', 'text', 'e.g. 2 turns')}
                    {settingFieldVisible('tokens') && fi('Tokens / Volume Spacers', 'b_tokens', 'text', 'e.g. 2 tokens')}
                    {['lsc','hsc','pedal','hbo'].some(settingFieldVisible) && sh('Compression', 'var(--muted)')}
                    {settingFieldVisible('lsc') && fi('LSC', 'b_lsc', 'text', 'e.g. 8 clicks from open')}
                    {settingFieldVisible('hsc') && fi('HSC', 'b_hsc', 'text', 'e.g. 3 turns from closed')}
                    {settingFieldVisible('pedal') && fi('Pedal / Lockout / Threshold', 'b_pedal', 'text', 'e.g. Position 2')}
                    {settingFieldVisible('hbo') && fi('HBO / HBC / ABO', 'b_hbo', 'text', 'if applicable')}
                    {['lsr','hsr'].some(settingFieldVisible) && sh('Rebound', 'var(--muted)')}
                    {settingFieldVisible('lsr') && fi('LSR / Rebound', 'b_lsr', 'text', 'e.g. 10 clicks from open')}
                    {settingFieldVisible('hsr') && fi('HSR', 'b_hsr', 'text', 'if applicable')}
                  </div>
                  <div>
                    {hasMainSettings && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>After Service</div>}
                    {['air','sag','preload','tokens'].some(settingFieldVisible) && sh('Spring', 'var(--green)')}
                    {settingFieldVisible('air') && fi('Air Pressure / Spring Rate', 'a_air', 'text', 'e.g. 75 psi')}
                    {settingFieldVisible('sag') && fi('Sag %', 'a_sag', 'text', 'e.g. 30%')}
                    {settingFieldVisible('preload') && fi('Coil Preload (turns)', 'a_preload', 'text', 'e.g. 2 turns')}
                    {settingFieldVisible('tokens') && fi('Tokens / Volume Spacers', 'a_tokens', 'text', 'e.g. 2 tokens')}
                    {['lsc','hsc','pedal','hbo'].some(settingFieldVisible) && sh('Compression', 'var(--green)')}
                    {settingFieldVisible('lsc') && fi('LSC', 'a_lsc', 'text', 'e.g. 8 clicks from open')}
                    {settingFieldVisible('hsc') && fi('HSC', 'a_hsc', 'text', 'e.g. 3 turns from closed')}
                    {settingFieldVisible('pedal') && fi('Pedal / Lockout / Threshold', 'a_pedal', 'text', 'e.g. Position 2')}
                    {settingFieldVisible('hbo') && fi('HBO / HBC / ABO', 'a_hbo', 'text', 'if applicable')}
                    {['lsr','hsr'].some(settingFieldVisible) && sh('Rebound', 'var(--green)')}
                    {settingFieldVisible('lsr') && fi('LSR / Rebound', 'a_lsr', 'text', 'e.g. 10 clicks from open')}
                    {settingFieldVisible('hsr') && fi('HSR', 'a_hsr', 'text', 'if applicable')}
                  </div>
                </div>
                {!hasMainSettings && <div className="fs12 text-muted" style={{ marginBottom: 10 }}>No standard settings are required for this template.</div>}
                {hasOptionalSettings && (
                  <details style={{ marginTop: 8 }}>
                    <summary className="fs12 text-muted fw7" style={{ cursor: 'pointer' }}>Optional / advanced settings</summary>
                    <div className="grid-2" style={{ marginTop: 12 }}>
                      {renderSettingsColumn('b', 'Before Service', 'var(--text-2)', true)}
                      {renderSettingsColumn('a', 'After Service', 'var(--green)', true)}
                    </div>
                  </details>
                )}
              </div>
            )}
            {renderAdditionalServiceInformation()}
          </>
        ))}

        {renderJobSection('inspection', 'Inspection & Condition', form.inspection_completed_at ? 'Signed off' : 'Awaiting sign-off', (
        <div style={sectionPanelStyle}>
        {renderCheckGrid(INSPECTION_FIELDS, 'inspection', inspectionFieldIncluded)}
        <div className="field" style={{ marginTop: 12 }}>
          <label>Condition Notes</label>
          <textarea value={form.condition_notes||''} onChange={e => set('condition_notes', e.target.value)} placeholder="Detail any damage, wear or observations on intake condition…" />
        </div>
        <div className="field">
          <label>Inspection Summary</label>
          <textarea value={form.inspection_summary||''} onChange={e => set('inspection_summary', e.target.value)} placeholder="Short workshop summary of what was found." style={{ minHeight: 56 }} />
        </div>
        {form.inspection_completed_at && (
          <div className="alert alert-success" style={{ marginTop: 12 }}>
            <strong>Inspection completed</strong> on {new Date(form.inspection_completed_at).toLocaleString('en-GB')}
          </div>
        )}
        {isEdit && canInspect && (
          <div className="action-row" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={saveInspectionWorkflow} disabled={workflowSaving}>
              {workflowSaving ? 'Saving...' : form.inspection_completed_at ? 'Update Inspection Sign-Off' : 'Complete Inspection'}
            </button>
          </div>
        )}
        </div>
        ))}

        {templateServiceLogSection}

        {renderJobSection('quote', inheritedServicePrices ? 'Reviewed Service Quote' : 'Bespoke Additional Quote', quoteSectionSummary, (
        <div style={sectionPanelStyle}>
          <div className="alert alert-info" style={{ marginBottom: 12 }}>
            {inheritedServicePrices ? 'Sends the recorded service prices for customer approval. Save operational edits separately. Additional amounts require attributable review.' : 'Use this for one-off work outside the standard service menu. Stock / Materials is for physical items, and Service Kit stays suspension-only.'}
          </div>
          <div className="grid-3">
            {!inheritedServicePrices && fi('Bespoke Quote (£)', 'estimate_amount', 'number', '0.00')}
            {!inheritedServicePrices && fi('Bespoke Parts Cost (£)', 'estimate_parts_cost', 'number', '0.00')}
            <div className="field">
              <label>Quote Status</label>
              <input readOnly value={quoteStatusLabel} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Customer Email</label>
              <input type="email" value={quoteEmail} onChange={e => setQuoteEmail(e.target.value)} placeholder="customer@email.com" />
            </div>
            <div className="field">
              <label>Quote Sent</label>
              <input readOnly value={form.estimate_sent_at ? new Date(form.estimate_sent_at).toLocaleString('en-GB') : ''} />
            </div>
          </div>
          <div className="field">
            <label>{inheritedServicePrices ? 'Quote Notes' : 'Bespoke Quote Notes'}</label>
            <textarea value={form.estimate_notes || ''} onChange={e => set('estimate_notes', e.target.value)} placeholder="One-off work, upgrade or request outside the standard service menu..." />
          </div>
          {isEdit && canSendQuote && (
            <div className="action-row">
              <button className="btn btn-primary btn-sm" onClick={sendQuoteEmail} disabled={workflowSaving}>{workflowSaving ? 'Sending...' : 'Send Quote Email'}</button>
              {!inheritedServicePrices && <button className="btn btn-ghost btn-sm" onClick={markQuoteSentWithoutEmail} disabled={workflowSaving}>{workflowSaving ? 'Saving...' : 'Mark Sent Without Email'}</button>}
              {canRecordDecision && !inheritedServicePrices && <button className="btn btn-green btn-sm" onClick={() => openWorkflow('decision')} disabled={workflowSaving}>Record Decision</button>}
            </div>
          )}
          {workflowMode === 'decision' && (
            <div style={{ marginTop: 14 }}>
              <div className="grid-2">
                <div className="field">
                  <label>Customer Decision</label>
                  <select value={form.customer_decision || 'approved'} onChange={e => set('customer_decision', e.target.value)}>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
                <div className="field">
                  <label>Decision Note</label>
                  <textarea value={form.customer_decision_note || ''} onChange={e => set('customer_decision_note', e.target.value)} />
                </div>
              </div>
              <div className="action-row">
                <button className="btn btn-primary btn-sm" onClick={recordCustomerDecision} disabled={workflowSaving}>{workflowSaving ? 'Saving...' : 'Save Decision'}</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setWorkflowMode(null)} disabled={workflowSaving}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        ))}

        {renderJobSection('evidence', 'Photos, Files & Service Evidence', isEdit ? 'Photos, videos and files' : 'Save the job first', (
        <div style={sectionPanelStyle}>
        {isEdit ? (
          <JobEvidence jobId={initialData.id} workshopId={workshopId} mode="edit" />
        ) : (
          <div className="fs12 text-muted">Save the job before adding photos, videos or files.</div>
        )}
        </div>
        ))}

        {renderJobSection('qc', 'Final QC', qcSummary, (
        <div style={sectionPanelStyle}>
          {renderCheckGrid(QC_FIELDS, 'qc', qcFieldIncluded)}
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field">
              <label>QC Result</label>
              <input
                readOnly
                value={
                  form.qc_status === 'passed'
                    ? 'Passed'
                    : form.qc_status === 'failed'
                      ? 'Failed'
                      : 'Not signed off'
                }
              />
            </div>
            <div className="field">
              <label>Signed Off</label>
              <input
                readOnly
                value={form.qc_signed_off_at
                  ? `${form.qc_signed_off_by_name || 'Workshop'} - ${new Date(form.qc_signed_off_at).toLocaleString('en-GB')}`
                  : 'Will be set when signed'}
              />
            </div>
          </div>
          <div className="field">
            <label>QC Notes</label>
            <textarea
              value={form.qc_notes || ''}
              onChange={e => set('qc_notes', e.target.value)}
              placeholder="Any QC issue, retest note, or handover detail..."
            />
          </div>
          {isEdit && canSignOffQc && (
            <div className="action-row">
              <button className="btn btn-green btn-sm" onClick={() => saveQcWorkflow('passed')} disabled={workflowSaving}>{workflowSaving ? 'Saving...' : 'Pass & Sign Off'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => saveQcWorkflow('failed')} disabled={workflowSaving} style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>{workflowSaving ? 'Saving...' : 'Fail QC'}</button>
              {form.qc_signed_off_at && <button className="btn btn-ghost btn-sm" onClick={clearQcSignOff} disabled={workflowSaving}>Clear Sign-Off</button>}
            </div>
          )}
          {form.qc_status && (
            <div className={`alert ${form.qc_status === 'passed' ? 'alert-success' : 'alert-error'}`} style={{ marginTop: 12 }}>
              <strong>{form.qc_status === 'passed' ? 'QC passed' : 'QC failed'}</strong>
              {form.qc_signed_off_by_name ? ` by ${form.qc_signed_off_by_name}` : ''}
              {form.qc_signed_off_at ? ` on ${new Date(form.qc_signed_off_at).toLocaleString('en-GB')}` : ''}
              {form.qc_notes && <div style={{ marginTop: 6, lineHeight: 1.5 }}>{form.qc_notes}</div>}
            </div>
          )}
        </div>
        ))}

        {renderJobSection('handover', 'Recommendation & Handover', form.next_service_date ? `Next due ${form.next_service_date}` : 'Service reminders and customer notes', (
        <>
        <div style={sectionPanelStyle}>
        <div className="grid-2">
          {handoverFieldVisible('interval') && fi('Service Interval Advised', 'interval', 'text', 'e.g. 50hrs or 6 months')}
          {handoverFieldVisible('next_service') && fi('Next Service Notes', 'next_service', 'text', 'e.g. Oct 2026 or 100hrs')}
          {handoverFieldVisible('next_service_date') && <div className="field">
            <label>Next Service Date <span style={{ color: 'var(--muted)', fontWeight: 400, textTransform: 'none' }}>(for automatic reminder email)</span></label>
            <input type="date" value={form.next_service_date||''} onChange={e => set('next_service_date', e.target.value)} />
          </div>}
        </div>
        </div>
        {handoverFieldVisible('further_work') && <div className="field"><label>Further Work Required</label><textarea value={form.further_work||''} onChange={e => set('further_work', e.target.value)} placeholder="List any follow-up work recommended…" style={{ minHeight: 56 }} /></div>}
        {handoverFieldVisible('worn_parts') && <div className="field"><label>Worn Parts Noted</label><textarea value={form.worn_parts||''} onChange={e => set('worn_parts', e.target.value)} placeholder="List any worn parts flagged…" style={{ minHeight: 56 }} /></div>}
        {handoverFieldVisible('setup_rec') && <div className="field"><label>Setup Recommendation</label><textarea value={form.setup_rec||''} onChange={e => set('setup_rec', e.target.value)} placeholder="Tuning notes or setup changes for the rider…" style={{ minHeight: 56 }} /></div>}
        </>
        ))}

        {renderJobSection('stockMaterials', 'Stock / Materials & Parts Used', partRows.length || stockUsageRows.length ? `${partRows.length + stockUsageRows.length} item${partRows.length + stockUsageRows.length === 1 ? '' : 's'} recorded` : 'Optional physical items', (
          renderPartsUsedEditor()
        ))}

        {renderJobSection('financials', 'Pricing & Job Admin', `Total GBP ${displayedTotal.toFixed(2)}`, (
        <div style={sectionPanelStyle}>
        <VatIntakeTotals intake={vatIntake} />
        {renderPriceAdjustmentsEditor()}
        <div className="grid-3">
          <div className="field">
            <label>Parts Cost (£)</label>
            <input readOnly value={'£' + financials.basePartsCost.toFixed(2)} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
          </div>
          {vatIntake.prices ? <div className="field"><label>Service Cost (including any VAT)</label><input readOnly value={vatIntake.gross.toFixed(2)} /></div> : fi('Service Cost (£)', 'labour_charge', 'number', '0.00')}
          <div className="field">
            <label>Bespoke Quote (£)</label>
            <input readOnly value={financials.effectiveQuoteAmount ? '£' + financials.effectiveQuoteAmount.toFixed(2) : ''} />
          </div>
        </div>
        <div className="grid-3">
          <div className="field">
            <label>Bespoke Parts Cost (£)</label>
            <input readOnly value={financials.effectiveQuotePartsCost ? '£' + financials.effectiveQuotePartsCost.toFixed(2) : ''} />
          </div>
          <div className="field">
            <label>Quote Status</label>
            <input readOnly value={quoteStatusLabel} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
          </div>
          <div className="field">
            <label>Quote Sent</label>
            <input readOnly value={form.estimate_sent_at ? new Date(form.estimate_sent_at).toLocaleString('en-GB') : ''} />
          </div>
        </div>
        {form.estimate_notes && (
          <div className="field">
            <label>{inheritedServicePrices ? 'Quote Notes' : 'Bespoke Quote Notes'}</label>
            <textarea readOnly value={form.estimate_notes} style={{ minHeight: 56 }} />
          </div>
        )}
        <div className="grid-3">
          <div className="field">
            <label>Subtotal (£)</label>
            <input readOnly value={financials.subtotal ? '£' + financials.subtotal.toFixed(2) : ''} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
          </div>
          <div className="field">
            <label>Approved Discounts (£)</label>
            <input readOnly value={financials.approvedDiscountTotal ? '-£' + financials.approvedDiscountTotal.toFixed(2) : ''} style={{ background: 'var(--black)', color: 'var(--orange)', fontWeight: 700 }} />
          </div>
          <div className="field">
            <label>Approved Adjustments (£)</label>
            <input readOnly value={financials.approvedSurchargeTotal ? '+£' + financials.approvedSurchargeTotal.toFixed(2) : ''} style={{ background: 'var(--black)', color: 'var(--green)', fontWeight: 700 }} />
          </div>
        </div>
        <div className="field">
          <label>Total Cost (£)</label>
          <input readOnly value={displayedTotal ? '£' + displayedTotal.toFixed(2) : ''} style={{ background: 'var(--black)', color: 'var(--white)', fontWeight: 700 }} />
        </div>
        <div className="grid-2">
          <p>Verified margin uses net sales and recorded costs. <a href="/reporting/profitability">View Job Profitability</a></p>
        </div>
        </div>
        ))}

        {handoverFieldVisible('notes') && renderJobSection('notes', 'Internal Notes', 'Private workshop notes', (
          <div style={sectionPanelStyle}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Internal Notes</label>
              <textarea value={form.notes||''} onChange={e => set('notes', e.target.value)} placeholder="Any additional observations or context…" />
            </div>
          </div>
        ))}

        <div className="modal-action-bar">
          <div className="modal-action-bar-inner">
          <div className="action-row">
            <button className="btn btn-primary" onClick={save} disabled={saving || deleting || vatIntake.blocked}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Service Record'}</button>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
          {isEdit && protectedDeleteReason && (
            <div className="alert alert-info" style={{ margin: '8px 0 0' }}>{protectedDeleteReason}</div>
          )}
          {isEdit && !protectedDeleteReason && !confirmDelete && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => setConfirmDelete(true)}>Delete Job</button>
          )}
          {isEdit && !protectedDeleteReason && confirmDelete && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="fs12 text-muted">Permanently delete this unprotected draft/test service record?</span>
              <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff' }} onClick={doDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function JobModal(props) {
  if (isGeneralJob(props.initialData)) {
    return <GeneralJobModal {...props} />
  }
  return <SuspensionJobModal {...props} />
}
